import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import WelcomeWizard from '../components/WelcomeWizard';
import InvoiceImportModal from '../components/InvoiceImportModal';
import { 
  getDashboard, 
  getLancamentos, 
  deleteLancamento, 
  updateLancamento, 
  createLancamento, 
  getContas, 
  togglePagoLancamento,
  getCategorias,
  getSubcategorias,
  getEntradasProjetivas,
  createEntradasProjetivasBulk,
  deleteEntradaProjetiva,
  generatePixSubscription,
  checkSubscriptionStatus,
  getPerfil
} from '../services/api';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Dashboard() {
  const [dashboardData, setDashboardData] = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [contas, setContas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProLimitModal, setShowProLimitModal] = useState(false);
  const [proLimitMessage, setProLimitMessage] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [editingLancamento, setEditingLancamento] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [mostrarValores, setMostrarValores] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagoItem, setPagoItem] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Checkout PRO
  const [pixData, setPixData] = useState(null);
  const [isGeneratingPix, setIsGeneratingPix] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'PENDING', 'PAID', 'EXPIRED'
  const [subscriptionPrice, setSubscriptionPrice] = useState('9.99');
  const pollingRef = useRef(null);
  
  const filterRef = useRef(null);
  const [entradasProjetivas, setEntradasProjetivas] = useState([]);
  const [showModalProjetivas, setShowModalProjetivas] = useState(false);
  const [modoGraficoProjetivo, setModoGraficoProjetivo] = useState(false);
  const [usarSaldoAnterior, setUsarSaldoAnterior] = useState(false);
  const [projetivasList, setProjetivasList] = useState([{ data: new Date().toLocaleDateString('en-CA'), valor: '', descricao: '' }]);
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);
  
  const totalProjetivas = entradasProjetivas.reduce((sum, p) => sum + parseFloat(p.valor || 0), 0);
  
  // Define o mês atual como filtro padrão (YYYY-MM)
  const mesAtual = new Date().toLocaleDateString('en-CA').slice(0, 7);
  const [filterMes, setFilterMes] = useState(mesAtual);
  const [filterTipo, setFilterTipo] = useState('TODOS');
  const [filterCategoria, setFilterCategoria] = useState('TODAS');
  const [filterSubcategoria, setFilterSubcategoria] = useState('TODAS');
  const [filterConta, setFilterConta] = useState('TODAS');
  const [filterAtraso, setFilterAtraso] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);

  // Custom Month Picker States
  const monthPickerRef = useRef(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    tipo: 'saida',
    data: new Date().toLocaleDateString('en-CA'),
    conta_id: '',
    conta_destino_id: '',
    categoria_id: '',
    subcategoria_id: '',
    pago: false,
    parcelado: false,
    recorrente: false,
    num_parcelas: 1
  });
  const [selectedContaInfo, setSelectedContaInfo] = useState(null);
  const [quickAddType, setQuickAddType] = useState(null); // 'entrada' ou 'saida'
  const [selectedContas, setSelectedContas] = useState([]);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (formData.conta_id) {
      const conta = contas.find(c => String(c.id) === String(formData.conta_id));
      setSelectedContaInfo(conta || null);
    } else {
      setSelectedContaInfo(null);
    }
  }, [formData.conta_id, contas]);

  // Custom Month Picker logic
  useEffect(() => {
    if (filterMes && filterMes !== 'TODOS') {
      const [year] = filterMes.split('-').map(Number);
      setPickerYear(year);
    } else {
      setPickerYear(new Date().getFullYear());
    }
  }, [filterMes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(event.target)) {
        setShowMonthPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFilterMesLabel = () => {
    if (!filterMes || filterMes === 'TODOS') return 'Todos os meses';
    const [year, monthNum] = filterMes.split('-');
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${meses[parseInt(monthNum, 10) - 1]}/${year}`;
  };

  const handlePrevYear = (e) => {
    e.stopPropagation();
    setPickerYear(prev => prev - 1);
  };

  const handleNextYear = (e) => {
    e.stopPropagation();
    setPickerYear(prev => prev + 1);
  };

  const handleSelectMonth = (monthIndex) => {
    const formattedMonth = String(monthIndex + 1).padStart(2, '0');
    setFilterMes(`${pickerYear}-${formattedMonth}`);
    setShowMonthPicker(false);
  };

  const handleSelectTodos = () => {
    setFilterMes('TODOS');
    setShowMonthPicker(false);
  };

  const triggerToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  useEffect(() => {
    checkOnboarding();
    loadDashboard();
    loadLancamentos();
    loadContas();
    loadCategorias();
    loadEntradasProjetivas();
    loadSubPrice();
    // eslint-disable-next-line
  }, []);

  const checkOnboarding = async () => {
    try {
      const response = await getPerfil();
      if (!response.data.onboarding_completed) {
        setShowWizard(true);
      }
    } catch (e) {
      console.error('Erro ao verificar onboarding:', e);
    }
  };

  const handleFinishWizard = () => {
    setShowWizard(false);
    triggerToast('Onboarding concluído com sucesso! 🎉');
    loadDashboard();
    loadLancamentos();
    loadContas();
    loadEntradasProjetivas();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLancamento(null);
    setQuickAddType(null);
    setFormData({
      descricao: '',
      valor: '',
      tipo: 'saida',
      data: new Date().toLocaleDateString('en-CA'),
      conta_id: '',
      conta_destino_id: '',
      categoria_id: '',
      subcategoria_id: '',
      pago: false,
      parcelado: false,
      recorrente: false,
      num_parcelas: 1
    });
    setSubcategorias([]);
  };

  const loadSubPrice = async () => {
    try {
      const { getPublicSubscriptionConfig } = await import('../services/api');
      const response = await getPublicSubscriptionConfig();
      setSubscriptionPrice(response.data.preco_assinatura);
    } catch (e) {
      console.error('Erro ao carregar preço da assinatura:', e);
    }
  };

  const loadEntradasProjetivas = async () => {
    try {
      const response = await getEntradasProjetivas();
      setEntradasProjetivas(response.data);
    } catch (error) {
      console.error('Erro ao carregar entradas projetivas:', error);
    }
  };


  const handleFilterCategoriaChange = (e) => {
    const categId = e.target.value;
    setFilterCategoria(categId);
    setFilterSubcategoria('TODAS');
    
    if (categId === 'TODAS') {
      setSubcategorias([]);
    } else {
      // Extrair subcategorias da categoria selecionada sem duplicatas
      const subCatsMap = new Map();
      lancamentos
        .filter(l => String(l.categoria_id) === String(categId) && l.subcategoria_nome)
        .forEach(l => {
          if (!subCatsMap.has(l.subcategoria_id)) {
            subCatsMap.set(l.subcategoria_id, { id: l.subcategoria_id, nome: l.subcategoria_nome });
          }
        });
      setSubcategorias(Array.from(subCatsMap.values()));
    }
  };

  const handleFilterTipoChange = (e) => {
    setFilterTipo(e.target.value);
    setFilterCategoria('TODAS');
    setFilterSubcategoria('TODAS');
    setSubcategorias([]);
  };

  const getCategoriasDisponiveis = () => {
    let cats = lancamentos;
    
    if (filterTipo !== 'TODOS') {
      cats = cats.filter(l => l.tipo === filterTipo);
    }
    
    const categoriasSet = new Map();
    cats.forEach(l => {
      if (l.categoria_id) {
        categoriasSet.set(l.categoria_id, { id: l.categoria_id, nome: l.categoria_nome });
      }
    });
    
    return Array.from(categoriasSet.values());
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleFilters = () => {
    const newState = !showFilters;
    setShowFilters(newState);
    
    // Se estiver abrindo, rolar até o filtro após um pequeno delay para o componente renderizar
    if (newState) {
      setTimeout(() => {
        filterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const loadCategorias = async () => {
    try {
      const response = await getCategorias();
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadSubcategoriasPorCategoria = async (categoriaId) => {
    if (!categoriaId) {
      setSubcategorias([]);
      return;
    }
    try {
      const response = await getSubcategorias(categoriaId);
      setSubcategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar subcategorias:', error);
      setSubcategorias([]);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    }
  };

  const handleRefreshData = async () => {
    try {
      triggerToast('Atualizando dados... 🔄');
      await Promise.all([
        loadDashboard(),
        loadLancamentos(),
        loadContas(),
        loadCategorias(),
        loadEntradasProjetivas()
      ]);
      triggerToast('Dados atualizados! 🚀');
    } catch (e) {
      triggerToast('Erro ao atualizar os dados', 'error');
    }
  };

  const handleUpgradeClick = () => {
    setProLimitMessage('Esta funcionalidade de análise avançada está disponível apenas para usuários PRO.');
    setShowProLimitModal(true);
    setPixData(null);
    setPaymentStatus(null);
  };

  const handleStartSubscription = async () => {
    try {
      setIsGeneratingPix(true);
      const response = await generatePixSubscription();
      setPixData(response.data);
      setPaymentStatus('PENDING');
      startPaymentPolling(response.data.txid);
    } catch (error) {
      triggerToast('Erro ao gerar PIX. Verifique se a chave PIX está configurada.', 'error');
    } finally {
      setIsGeneratingPix(false);
    }
  };

  const startPaymentPolling = (txid) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    
    pollingRef.current = setInterval(async () => {
      try {
        const response = await checkSubscriptionStatus(txid);
        if (response.data.status === 'PAID') {
          setPaymentStatus('PAID');
          clearInterval(pollingRef.current);
          triggerToast('Parabéns! Você agora é PRO!', 'success');
          
          // Atualizar usuário no localStorage
          const updatedUser = { ...user, is_pro: true };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          // Recarregar após 3 segundos
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      } catch (error) {
        console.error('Erro no polling:', error);
      }
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const loadLancamentos = async () => {
    try {
      const response = await getLancamentos();
      setLancamentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar lançamentos:', error);
    }
  };

  const loadContas = async () => {
    try {
      const response = await getContas();
      setContas(response.data);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
    }
  };

  const handleEdit = (lancamento) => {
    setEditingLancamento(lancamento);
    
    if (lancamento.categoria_id) {
      loadSubcategoriasPorCategoria(lancamento.categoria_id);
    } else {
      setSubcategorias([]);
    }

    setFormData({
      descricao: lancamento.descricao,
      valor: lancamento.valor,
      tipo: lancamento.tipo,
      data: lancamento.data.split('T')[0],
      conta_id: lancamento.conta_id,
      conta_destino_id: lancamento.conta_destino_id || '',
      categoria_id: lancamento.categoria_id || '',
      subcategoria_id: lancamento.subcategoria_id || '',
      pago: lancamento.pago,
      parcelado: lancamento.parcelado || false,
      num_parcelas: lancamento.num_parcelas || 1
    });
    setShowModal(true);
  };

  const handleDelete = (lancamento) => {
    setItemToDelete({ type: 'lancamento', item: lancamento });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (itemToDelete.type === 'lancamento') {
        await deleteLancamento(itemToDelete.item.id);
        triggerToast('Lançamento excluído com sucesso!');
        loadLancamentos();
      } else if (itemToDelete.type === 'projetiva') {
        await deleteEntradaProjetiva(itemToDelete.item.id);
        triggerToast('Entrada projetiva excluída!');
        loadEntradasProjetivas();
      }
      setShowDeleteModal(false);
      setItemToDelete(null);
      loadDashboard();
    } catch (error) {
      triggerToast('Erro ao excluir item', 'error');
      setShowDeleteModal(false);
    }
  };

  const handleTogglePago = (lancamento) => {
    setPagoItem(lancamento);
    setShowPagoModal(true);
  };

  const confirmTogglePago = async () => {
    try {
      await togglePagoLancamento(pagoItem.id);
      triggerToast(`Status alterado: ${!pagoItem.pago ? 'Pago' : 'Pendente'}`);
      setShowPagoModal(false);
      setPagoItem(null);
      loadLancamentos();
      loadDashboard();
      loadContas();
    } catch (error) {
      triggerToast('Erro ao alterar status de pagamento', 'error');
      setShowPagoModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLancamento) {
        await updateLancamento(editingLancamento.id, formData);
      } else {
        await createLancamento(formData);
      }
      
      handleCloseModal();
      triggerToast(editingLancamento ? 'Lançamento atualizado!' : 'Lançamento criado!');
      loadLancamentos();
      loadDashboard();
      loadContas();
    } catch (error) {
      if (error.response?.status === 403) {
        setProLimitMessage(error.response.data.error);
        setShowProLimitModal(true);
        setShowModal(false);
      } else {
        console.error(error);
        triggerToast(editingLancamento ? 'Erro ao atualizar lançamento' : 'Erro ao criar lançamento', 'error');
      }
    }
  };

  // Handlers para Entradas Projetivas
  const handleAddProjetivaRow = () => {
    setProjetivasList([...projetivasList, { data: new Date().toLocaleDateString('en-CA'), valor: '', descricao: '' }]);
  };
  
  const handleRemoveProjetivaRow = (index) => {
    const list = [...projetivasList];
    list.splice(index, 1);
    setProjetivasList(list);
  };

  const handleProjetivaChange = (index, field, value) => {
    const list = [...projetivasList];
    list[index][field] = value;
    setProjetivasList(list);
  };

  const handleCalendarDayClick = (dateString) => {
    if (selectedCalendarDay === dateString) {
      setSelectedCalendarDay(null);
    } else {
      setSelectedCalendarDay(dateString);
      const mesDoDia = dateString.slice(0, 7);
      if (filterMes === 'TODOS') {
        setFilterMes(mesDoDia);
      }
      if (!showFilters) {
        setShowFilters(true);
      }
    }
  };

  const calcularScoreSaudeFinanceira = () => {
    let score = 100;
    const { totalEntradas, totalSaidas } = calcularTotaisFiltrados();
    const emAtraso = calcularEmAtraso();

    if (emAtraso.quantidade > 0) {
      score -= Math.min(40, emAtraso.quantidade * 10);
    }

    if (totalEntradas > 0) {
      const taxaPoupança = ((totalEntradas - totalSaidas) / totalEntradas) * 100;
      if (taxaPoupança < 0) {
        score -= 30;
      } else if (taxaPoupança < 20) {
        score -= 10;
      }
    } else if (totalSaidas > 0) {
      score -= 40;
    }

    const categoriasValores = calcularValoresPorCategoria();
    let categoriasEstouradas = 0;
    categoriasValores.forEach(c => {
      if (c.meta && c.total > c.meta) {
        categoriasEstouradas++;
      }
    });
    if (categoriasEstouradas > 0) {
      score -= Math.min(20, categoriasEstouradas * 10);
    }

    score = Math.max(0, Math.min(100, score));

    let status = 'excelente';
    let cor = '#10b981';
    let recomendacao = 'Excelente! Seu orçamento está sob controle e você está poupando dinheiro. Que tal investir o excedente? 🚀';

    if (score < 50) {
      status = 'critico';
      cor = '#ef4444';
      recomendacao = 'Atenção! Você está gastando mais do que ganha ou tem contas vencidas. É crucial cortar despesas e renegociar metas. ⚠️';
    } else if (score < 80) {
      status = 'alerta';
      cor = '#f59e0b';
      recomendacao = 'Alerta! Suas despesas estão elevadas ou há contas pendentes. Evite compras supérfluas e acompanhe suas metas por categoria. 🔍';
    }

    return { score, status, cor, recomendacao };
  };

  const calcularTaxaPoupanca = () => {
    const { totalEntradas, totalSaidas } = calcularTotaisFiltrados();
    if (totalEntradas <= 0) return 0;
    return ((totalEntradas - totalSaidas) / totalEntradas) * 100;
  };
  
  const handleSaveProjetivas = async () => {
    try {
      const validItems = projetivasList.filter(item => item.descricao && item.valor && item.data);
      if (validItems.length > 0) {
        await createEntradasProjetivasBulk(validItems);
        triggerToast('Entradas projetivas salvas! 📈');
        loadEntradasProjetivas();
      }
      setShowModalProjetivas(false);
      setProjetivasList([{ data: new Date().toLocaleDateString('en-CA'), valor: '', descricao: '' }]);
    } catch (e) {
      triggerToast('Erro ao salvar entradas projetivas', 'error');
    }
  };

  const handleDeleteProjetivaDb = (id) => {
    setItemToDelete({ type: 'projetiva', item: { id } });
    setShowDeleteModal(true);
  };


  const handleLimparFiltros = () => {
    setFilterMes(mesAtual);
    setFilterTipo('TODOS');
    setFilterCategoria('TODAS');
    setFilterSubcategoria('TODAS');
    setFilterConta('TODAS');
    setFilterAtraso(false);
    setSubcategorias([]);
    setSelectedCalendarDay(null);
  };

  const calcularFaturaMes = (contaId, mesFiltro = null) => {
    const mesParaFiltro = mesFiltro || (filterMes !== 'TODOS' ? filterMes : new Date().toLocaleDateString('en-CA').slice(0, 7));
    let totalGasto = 0;
    let totalPago = 0;

    lancamentos
      .filter(l => {
        const data = new Date(l.data);
        const mesLancamento = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
        if (mesLancamento !== mesParaFiltro) return false;
        
        // Se for gasto normal ou entrada direta no cartão
        if (Number(l.conta_id) === Number(contaId)) return true;
        
        // Se for um pagamento de fatura vindo de outra conta para este cartão
        if (l.tipo === 'pagamento_fatura' && Number(l.conta_destino_id) === Number(contaId)) return true;
        
        return false;
      })
      .forEach(l => {
        // Gasto: saídas na conta do cartão
        if (l.tipo === 'saida' && Number(l.conta_id) === Number(contaId)) totalGasto += parseFloat(l.valor);
        
        // Estorno: diminui o gasto do cartão
        if (l.tipo === 'estorno' && Number(l.conta_id) === Number(contaId)) totalGasto -= parseFloat(l.valor);
        
        // Pagamento: entradas na conta do cartão OU pagamento_fatura onde o destino é este cartão
        if (l.tipo === 'entrada' && Number(l.conta_id) === Number(contaId)) totalPago += parseFloat(l.valor);
        if (l.tipo === 'pagamento_fatura' && Number(l.conta_destino_id) === Number(contaId)) totalPago += parseFloat(l.valor);
      });

    return { 
      totalGasto, 
      totalPago, 
      saldoRestante: Math.round((totalGasto - totalPago) * 100) / 100 
    };
  };

  const calcularSaldoTotal = () => {
    return contas.reduce((total, conta) => {
      // Cartão de crédito é passivo/dívida, não entra no cálculo de patrimônio líquido/disponível neste dashboard
      if (conta.tipo === 'Cartão de Crédito') {
        return total;
      }
      return total + (Number(conta.saldo_inicial) || 0);
    }, 0);
  };

  const calcularSaldosPorTipo = () => {
    const tipos = {
      'Conta Corrente': 0,
      'Conta Poupança': 0,
      'Conta Investimento': 0,
      'Cartão de Crédito': 0,
      'Dinheiro': 0
    };

    contas.forEach(conta => {
      const tipo = conta.tipo || 'Conta Corrente';
      if (tipo === 'Cartão de Crédito') {
        tipos[tipo] = (tipos[tipo] || 0) + (Number(conta.saldo_inicial) || 0);
      } else {
        tipos[tipo] = (tipos[tipo] || 0) + (Number(conta.saldo_inicial) || 0);
      }
    });

    return tipos;
  };

  const gerarCalendario = () => {
    const hoje = new Date();
    let ano = hoje.getFullYear();
    let mes = hoje.getMonth();
    
    if (filterMes !== 'TODOS') {
      const [fAno, fMes] = filterMes.split('-');
      ano = parseInt(fAno, 10);
      mes = parseInt(fMes, 10) - 1;
    }
    
    const primeiroDia = new Date(ano, mes, 1).getDay();
    const ultimoDia = new Date(ano, mes + 1, 0).getDate();
    
    const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const mesesNomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                   'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    
    const dias = [];
    
    for (let i = 0; i < primeiroDia; i++) {
      dias.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    for (let dia = 1; dia <= ultimoDia; dia++) {
      const ehHoje = dia === hoje.getDate() && mes === hoje.getMonth() && ano === hoje.getFullYear();
      const dataChave = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      const ehSelecionado = selectedCalendarDay === dataChave;

      const lancamentosDoDia = lancamentos.filter(l => l.data.split('T')[0] === dataChave);
      const temEntrada = lancamentosDoDia.some(l => l.tipo === 'entrada');
      const saídas = lancamentosDoDia.filter(l => l.tipo === 'saida');
      const temSaidaPaga = saídas.some(l => l.pago || l.conta_tipo === 'Cartão de Crédito');
      const temSaidaPendente = saídas.some(l => !l.pago && l.conta_tipo !== 'Cartão de Crédito');

      dias.push(
        <div 
          key={dia} 
          className={`calendar-day ${ehHoje ? 'hoje' : ''} ${ehSelecionado ? 'selected' : ''} ${lancamentosDoDia.length > 0 ? 'has-events' : ''}`}
          onClick={() => handleCalendarDayClick(dataChave)}
        >
          <span className="day-number">{dia}</span>
          <div className="day-dots">
            {temEntrada && <span className="dot-green"></span>}
            {temSaidaPaga && <span className="dot-red"></span>}
            {temSaidaPendente && <span className="dot-yellow"></span>}
          </div>
        </div>
      );
    }
    
    return (
      <div className="calendar-widget">
        <div className="calendar-header">
          <span>{mesesNomes[mes]} {ano}</span>
          {selectedCalendarDay && (
            <button 
              className="btn-clear-calendar-filter" 
              onClick={(e) => { 
                e.stopPropagation(); 
                setSelectedCalendarDay(null); 
              }}
              title="Limpar filtro de data"
            >
              Limpar
            </button>
          )}
        </div>
        <div className="calendar-weekdays">
          {diasSemana.map(dia => (
            <div key={dia} className="calendar-weekday">{dia}</div>
          ))}
        </div>
        <div className="calendar-days">
          {dias}
        </div>
        <div className="calendar-legend">
          <div className="legend-item">
            <span className="legend-dot dot-green"></span>
            <span>Receita</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot dot-red"></span>
            <span>Pago</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot dot-yellow"></span>
            <span>Pendente</span>
          </div>
        </div>
      </div>
    );
  };

  const calcularTotaisFiltrados = () => {
    let filtrados = lancamentos;

    // Aplicar filtro de mês
    if (filterMes !== 'TODOS') {
      filtrados = filtrados.filter(l => {
        const data = new Date(l.data);
        const mesLancamento = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
        return mesLancamento === filterMes;
      });
    }

    // Aplicar filtro de tipo
    if (filterTipo !== 'TODOS') {
      filtrados = filtrados.filter(l => l.tipo === filterTipo);
    }

    // Aplicar filtro de categoria
    if (filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.categoria_id == filterCategoria);
    }

    // Aplicar filtro de subcategoria
    if (filterSubcategoria !== 'TODAS' && filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.subcategoria_id == filterSubcategoria);
    }

    // Aplicar filtro de conta
    if (filterConta !== 'TODAS') {
      filtrados = filtrados.filter(l => l.conta_id == filterConta || l.conta_destino_id == filterConta);
    }

    // Aplicar filtro de atraso
    if (filterAtraso) {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      filtrados = filtrados.filter(l => {
        if (l.tipo !== 'saida') return false;
        const isCard = l.conta_tipo === 'Cartão de Crédito';
        if (!isCard) {
          return !l.pago && new Date(l.data) < hoje;
        } else {
          const conta = contas.find(c => Number(c.id) === Number(l.conta_id));
          if (!conta || !conta.dia_vencimento) return false;
          
          const dataLancamento = new Date(l.data);
          const mesAno = `${dataLancamento.getFullYear()}-${String(dataLancamento.getMonth() + 1).padStart(2, '0')}`;
          const [year, month] = mesAno.split('-').map(Number);
          const dataVencimento = new Date(year, month - 1, conta.dia_vencimento, 23, 59, 59);
          if (dataVencimento >= hoje) return false;
          
          const { saldoRestante } = calcularFaturaMes(conta.id, mesAno);
          return saldoRestante > 0.01;
        }
      });
    }

    // Calcular totais
    const totalEntradas = filtrados
      .filter(l => l.tipo === 'entrada')
      .reduce((sum, l) => sum + parseFloat(l.valor), 0);

    const totalEstornos = filtrados
      .filter(l => l.tipo === 'estorno')
      .reduce((sum, l) => sum + parseFloat(l.valor), 0);

    const saidas = filtrados.filter(l => l.tipo === 'saida');
    
    const totalSaidas = Math.max(0, saidas.reduce((sum, l) => sum + parseFloat(l.valor), 0) - totalEstornos);

    const totalSaidasPagos = Math.max(0, saidas
      .filter(l => l.pago || l.conta_tipo === 'Cartão de Crédito')
      .reduce((sum, l) => sum + parseFloat(l.valor), 0) - totalEstornos);

    const totalSaidasPendentes = saidas
      .filter(l => !l.pago && l.conta_tipo !== 'Cartão de Crédito')
      .reduce((sum, l) => sum + parseFloat(l.valor), 0);

    return { totalEntradas, totalSaidas, totalSaidasPagos, totalSaidasPendentes };
  };

  const calcularEmAtraso = () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    let filtrados = lancamentos.filter(l => {
      const dataLancamento = new Date(l.data);
      const isCard = l.conta_tipo === 'Cartão de Crédito';
      return l.tipo === 'saida' && !l.pago && dataLancamento < hoje && !isCard;
    });

    // Aplicar filtro de mês se não for "TODOS"
    if (filterMes !== 'TODOS') {
      filtrados = filtrados.filter(l => {
        const data = new Date(l.data);
        const mesLancamento = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
        return mesLancamento === filterMes;
      });
    }

    let quantidade = filtrados.length;
    let valorTotal = filtrados.reduce((sum, l) => sum + parseFloat(l.valor), 0);

    // Calcular atrasos consolidados por cartão de crédito
    contas.forEach(conta => {
      if (conta.tipo === 'Cartão de Crédito') {
        const mesesComTransacoes = new Set();
        lancamentos.forEach(l => {
          if (Number(l.conta_id) === Number(conta.id) || (l.tipo === 'pagamento_fatura' && Number(l.conta_destino_id) === Number(conta.id))) {
            const data = new Date(l.data);
            const mesAno = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
            mesesComTransacoes.add(mesAno);
          }
        });

        mesesComTransacoes.forEach(mesAno => {
          if (filterMes !== 'TODOS' && mesAno !== filterMes) return;

          const [year, month] = mesAno.split('-').map(Number);
          const dataVencimento = new Date(year, month - 1, conta.dia_vencimento || 1, 23, 59, 59);
          
          if (dataVencimento < hoje) {
            const { saldoRestante } = calcularFaturaMes(conta.id, mesAno);
            if (saldoRestante > 0.01) {
              quantidade += 1;
              valorTotal += saldoRestante;
            }
          }
        });
      }
    });

    return { quantidade, valorTotal };
  };

  const formatarMoeda = (valor) => {
    return valor.toLocaleString('pt-BR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const toggleSelectConta = (contaId) => {
    setSelectedContas(prev => 
      prev.includes(contaId) 
        ? prev.filter(id => id !== contaId) 
        : [...prev, contaId]
    );
  };

  const calcularSomaSelecionadas = () => {
    let totalNormal = 0;
    let totalCC = 0;
    let hasNormal = false;
    let hasCC = false;

    contas
      .filter(c => selectedContas.includes(c.id))
      .forEach(conta => {
        if (conta.tipo === 'Cartão de Crédito') {
          totalCC += calcularFaturaMes(conta.id).saldoRestante;
          hasCC = true;
        } else {
          totalNormal += (Number(conta.saldo_inicial) || 0);
          hasNormal = true;
        }
      });

    return { totalNormal, totalCC, hasNormal, hasCC };
  };

  // Calcular valores por categoria e subcategoria com base no filtro
  const calcularValoresPorCategoria = () => {
    let filtrados = lancamentos;

    // Aplicar filtro de mês
    if (filterMes !== 'TODOS') {
      filtrados = filtrados.filter(l => {
        const data = new Date(l.data);
        const mesLancamento = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
        return mesLancamento === filterMes;
      });
    }

    // Aplicar filtro de tipo
    if (filterTipo !== 'TODOS') {
      if (filterTipo === 'saida') {
        filtrados = filtrados.filter(l => l.tipo === 'saida' || l.tipo === 'estorno');
      } else {
        filtrados = filtrados.filter(l => l.tipo === filterTipo);
      }
    } else {
      // Excluir transferências, neutros e pagamentos de fatura por padrão (são neutros no balanço de categorias)
      filtrados = filtrados.filter(l => l.tipo !== 'transferencia' && l.tipo !== 'neutro' && l.tipo !== 'pagamento_fatura');
    }

    // Aplicar filtro de categoria
    if (filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.categoria_id == filterCategoria);
    }

    // Aplicar filtro de subcategoria
    if (filterSubcategoria !== 'TODAS' && filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.subcategoria_id == filterSubcategoria);
    }

    // Aplicar filtro de conta
    if (filterConta !== 'TODAS') {
      filtrados = filtrados.filter(l => l.conta_id == filterConta || l.conta_destino_id == filterConta);
    }

    // Aplicar filtro de atraso
    if (filterAtraso) {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      filtrados = filtrados.filter(l => {
        if (l.tipo !== 'saida' || l.pago) return false;
        const dataLancamento = new Date(l.data);
        return dataLancamento < hoje;
      });
    }

    // Aplicar filtro de dia selecionado no calendário
    if (selectedCalendarDay) {
      filtrados = filtrados.filter(l => l.data.split('T')[0] === selectedCalendarDay);
    }

    // Agrupar por categoria e subcategoria
    const grupos = {};

    filtrados.forEach(lancamento => {
      const categoriaNome = lancamento.categoria_nome || 'Sem Categoria';
      const categoriaId = lancamento.categoria_id || 'sem-categoria';
      const categoriaCor = lancamento.categoria_cor || '#999999';
      const subcategoriaNome = lancamento.subcategoria_nome || 'Sem Subcategoria';
      const subcategoriaId = lancamento.subcategoria_id || 'sem-subcategoria';

      if (!grupos[categoriaId]) {
        const catOriginal = categorias.find(c => String(c.id) === String(categoriaId));
        grupos[categoriaId] = {
          id: categoriaId,
          nome: categoriaNome,
          cor: categoriaCor,
          total: 0,
          meta: catOriginal ? catOriginal.meta_mensal : null,
          subcategorias: {}
        };
      }

      const val = lancamento.tipo === 'estorno' ? -parseFloat(lancamento.valor) : parseFloat(lancamento.valor);
      grupos[categoriaId].total += val;

      if (!grupos[categoriaId].subcategorias[subcategoriaId]) {
        grupos[categoriaId].subcategorias[subcategoriaId] = {
          id: subcategoriaId,
          nome: subcategoriaNome,
          total: 0,
          meta: lancamento.subcategoria_meta || null
        };
      }

      grupos[categoriaId].subcategorias[subcategoriaId].total += val;
    });

    // Converter para array e ordenar por valor total
    return Object.values(grupos)
      .sort((a, b) => b.total - a.total)
      .map(categoria => ({
        ...categoria,
        subcategorias: Object.values(categoria.subcategorias)
          .sort((a, b) => b.total - a.total)
      }));
  };

  // Calcular totais de gastos, somatório de metas e economia potencial
  const calcularResumoMetas = () => {
    const catsFiltradas = calcularValoresPorCategoria();
    let totalGasto = 0;
    let somaMetaCategorias = 0;
    let somaMetaSubcategorias = 0;
    let economiaCategorias = 0;
    let economiaSubcategorias = 0;

    catsFiltradas.forEach(categoria => {
      // Procurar categoria original para verificar o tipo
      const catOriginal = categorias.find(c => String(c.id) === String(categoria.id));
      const isSaida = catOriginal ? catOriginal.tipo === 'saida' : true; 

      if (isSaida) {
        totalGasto += categoria.total;
        
        if (categoria.meta && Number(categoria.meta) > 0) {
          somaMetaCategorias += Number(categoria.meta);
          if (categoria.total > Number(categoria.meta)) {
            economiaCategorias += (categoria.total - Number(categoria.meta));
          }
        }
      }

      categoria.subcategorias.forEach(sub => {
        if (sub.meta && Number(sub.meta) > 0 && isSaida) {
          somaMetaSubcategorias += Number(sub.meta);
          if (sub.total > Number(sub.meta)) {
            economiaSubcategorias += (sub.total - Number(sub.meta));
          }
        }
      });
    });

    return {
      totalGasto,
      somaMetaCategorias,
      somaMetaSubcategorias,
      economiaCategorias,
      economiaSubcategorias
    };
  };

  // Processar dados para o gráfico
  const processChartData = () => {
    const target = filterMes && filterMes !== 'TODOS' ? filterMes : mesAtual;
    const [year, month] = target.split('-').map(Number);
    
    const meses = [];
    for (let i = -6; i <= 6; i++) {
      const d = new Date(year, month - 1 + i, 1);
      const yStr = d.getFullYear();
      const mStr = String(d.getMonth() + 1).padStart(2, '0');
      meses.push(`${yStr}-${mStr}`);
    }
    
    const entradas = meses.map(mes => {
      const item = dashboardData.find(d => d.mes === mes && d.tipo === 'entrada');
      return item ? parseFloat(item.total) : 0;
    });

    const saidas = meses.map(mes => {
      const item = dashboardData.find(d => d.mes === mes && d.tipo === 'saida');
      const estorno = dashboardData.find(d => d.mes === mes && d.tipo === 'estorno');
      const valSaida = item ? parseFloat(item.total) : 0;
      const valEstorno = estorno ? parseFloat(estorno.total) : 0;
      return Math.max(0, valSaida - valEstorno);
    });

    const balanco = meses.map((mes, i) => entradas[i] - saidas[i]);

    return {
      labels: meses.map(mes => {
        const [ano, mesNum] = mes.split('-');
        const mesesNomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        return `${mesesNomes[parseInt(mesNum)-1]}/${ano.slice(2)}`;
      }),
      datasets: [
        {
          type: 'line',
          label: 'Saldo do Mês',
          data: balanco,
          borderColor: '#6366f1',
          borderWidth: 2.5,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#6366f1',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4,
          fill: true,
          backgroundColor: 'rgba(99, 102, 241, 0.08)',
          order: 0,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Entradas',
          data: entradas,
          backgroundColor: 'rgba(34, 197, 94, 0.75)',
          hoverBackgroundColor: 'rgba(34, 197, 94, 0.95)',
          borderRadius: { topLeft: 6, topRight: 6 },
          borderSkipped: false,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
          order: 1,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Saídas',
          data: saidas,
          backgroundColor: 'rgba(239, 68, 68, 0.7)',
          hoverBackgroundColor: 'rgba(239, 68, 68, 0.95)',
          borderRadius: { topLeft: 6, topRight: 6 },
          borderSkipped: false,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
          order: 1,
          yAxisID: 'y',
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, weight: '600' }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 14,
        boxPadding: 6,
        usePointStyle: true,
        cornerRadius: 12,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11, weight: '600' },
          color: '#64748b',
          maxRotation: 45,
          minRotation: 0,
        },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        stacked: false,
        grid: { color: 'rgba(0, 0, 0, 0.04)', drawBorder: false },
        border: { display: false },
        ticks: {
          font: { size: 11 },
          color: '#94a3b8',
          callback: value => {
            if (Math.abs(value) >= 1000) {
              return 'R$ ' + (value / 1000).toFixed(0) + 'k';
            }
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        }
      }
    }
  };

  // Processar dados para o gráfico de pizza (saídas por categoria do mês filtrado)
  const processPieChartData = () => {
    const mesParaGrafico = filterMes !== 'TODOS' ? filterMes : new Date().toLocaleDateString('en-CA').slice(0, 7);
    const saidasPorCategoria = {};
    const coresCategoria = {};
    
    let filtrados = lancamentos.filter(l => l.tipo === 'saida' || l.tipo === 'estorno');

    if (filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.categoria_id == filterCategoria);
    }
    if (filterSubcategoria !== 'TODAS' && filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.subcategoria_id == filterSubcategoria);
    }
    if (filterConta !== 'TODAS') {
      filtrados = filtrados.filter(l => l.conta_id == filterConta);
    }
    
    filtrados.forEach(lancamento => {
      const mesLancamento = new Date(lancamento.data).toISOString().slice(0, 7);
      if (mesLancamento === mesParaGrafico) {
        const categoria = lancamento.categoria_nome || 'Sem Categoria';
        if (!saidasPorCategoria[categoria]) {
          saidasPorCategoria[categoria] = 0;
          coresCategoria[categoria] = lancamento.categoria_cor || '#999999';
        }
        const val = lancamento.tipo === 'estorno' ? -parseFloat(lancamento.valor) : parseFloat(lancamento.valor);
        saidasPorCategoria[categoria] += val;
      }
    });

    // Ordenar categorias por valor (do maior para o menor)
    const sortedCategories = Object.keys(saidasPorCategoria).sort((a, b) => saidasPorCategoria[b] - saidasPorCategoria[a]);
    const sortedData = sortedCategories.map(cat => saidasPorCategoria[cat]);
    const sortedColors = sortedCategories.map(cat => coresCategoria[cat]);
    
    return {
      labels: sortedCategories,
      datasets: [
        {
          data: sortedData,
          backgroundColor: sortedColors,
          borderColor: 'white',
          borderWidth: 3,
          hoverOffset: 15,
          spacing: 5,
        },
      ],
    };
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '80%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    }
  };

  // Processar dados para o gráfico de pizza (saídas por conta do mês filtrado)
  const processPieChartDataPorConta = () => {
    const mesParaGrafico = filterMes !== 'TODOS' ? filterMes : new Date().toLocaleDateString('en-CA').slice(0, 7);
    const saidasPorConta = {};
    
    // Cores para as contas (já que não possuem cor definida no banco)
    const coresDisponiveis = ['#6366f1', '#22c55e', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#0ea5e9'];
    const coresConta = {};
    let colorIndex = 0;

    let filtrados = lancamentos.filter(l => l.tipo === 'saida' || l.tipo === 'estorno');

    if (filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.categoria_id == filterCategoria);
    }
    if (filterSubcategoria !== 'TODAS' && filterCategoria !== 'TODAS') {
      filtrados = filtrados.filter(l => l.subcategoria_id == filterSubcategoria);
    }
    if (filterConta !== 'TODAS') {
      filtrados = filtrados.filter(l => l.conta_id == filterConta);
    }

    filtrados.forEach(lancamento => {
      const mesLancamento = new Date(lancamento.data).toISOString().slice(0, 7);
      if (mesLancamento === mesParaGrafico) {
        const contaNome = lancamento.conta_nome || 'Sem Conta';
        if (!saidasPorConta[contaNome]) {
          saidasPorConta[contaNome] = 0;
          coresConta[contaNome] = coresDisponiveis[colorIndex % coresDisponiveis.length];
          colorIndex++;
        }
        const val = lancamento.tipo === 'estorno' ? -parseFloat(lancamento.valor) : parseFloat(lancamento.valor);
        saidasPorConta[contaNome] += val;
      }
    });

    const sortedContas = Object.keys(saidasPorConta).sort((a, b) => saidasPorConta[b] - saidasPorConta[a]);
    const sortedData = sortedContas.map(conta => saidasPorConta[conta]);
    const sortedColors = sortedContas.map(conta => coresConta[conta]);
    
    return {
      labels: sortedContas,
      datasets: [
        {
          data: sortedData,
          backgroundColor: sortedColors,
          borderColor: 'white',
          borderWidth: 3,
          hoverOffset: 15,
          spacing: 5,
        },
      ],
    };
  };

  // Processar dados para o gráfico de Área (Evolução Diária)
  const processAreaChartData = () => {
    const mesParaGrafico = filterMes !== 'TODOS' ? filterMes : new Date().toLocaleDateString('en-CA').slice(0, 7);
    const [ano, mes] = mesParaGrafico.split('-');
    
    // Obter número de dias no mês
    const diasNoMes = new Date(ano, mes, 0).getDate();
    
    const labels = [];
    const saldosAcumulados = [];
    const entradasPorDia = [];
    const saidasPorDia = [];
    
    let saldoAtual = 0;

    if (usarSaldoAnterior) {
      // Calcular saldo de todos os lançamentos anteriores ao mês selecionado
      const lancamentosAnteriores = lancamentos.filter(l => {
        const dataLanc = new Date(l.data).toISOString().slice(0, 7);
        // Consideramos apenas entradas e saídas que já foram liquidadas (pagas) ou todas?
        // Geralmente para saldo real, usamos o que impactou a conta.
        return dataLanc < mesParaGrafico && (l.tipo === 'entrada' || l.tipo === 'saida' || l.tipo === 'estorno');
      });

      const entradasAnteriores = lancamentosAnteriores
        .filter(l => l.tipo === 'entrada')
        .reduce((sum, l) => sum + parseFloat(l.valor), 0);

      const saidasAnteriores = lancamentosAnteriores
        .filter(l => l.tipo === 'saida')
        .reduce((sum, l) => sum + parseFloat(l.valor), 0) -
        lancamentosAnteriores
        .filter(l => l.tipo === 'estorno')
        .reduce((sum, l) => sum + parseFloat(l.valor), 0);

      saldoAtual = entradasAnteriores - saidasAnteriores;
    }
    
    const lancamentosDoMes = lancamentos.filter(l => {
        const dataLanc = new Date(l.data).toISOString().slice(0, 7);
        return dataLanc === mesParaGrafico && (l.tipo === 'entrada' || l.tipo === 'saida' || l.tipo === 'estorno');
    });

    // Para as projetivas, não filtramos por mês/ano, pois elas valem para qualquer mês (apenas o dia importa)
    const entradasProjetivasGlobais = entradasProjetivas;

    for (let dia = 1; dia <= diasNoMes; dia++) {
      labels.push(`${dia}/${mes}`);
      
      const diaFormatado = `${ano}-${mes}-${String(dia).padStart(2, '0')}`;
      
      const lancamentosDoDia = lancamentosDoMes.filter(l => l.data.startsWith(diaFormatado));
      
      let entradasDia = 0;
      
      if (modoGraficoProjetivo) {
        // Filtra projetivas que coincidem com o dia do mês atual (independente de ano/mês do cadastro)
        const projetivasDoDia = entradasProjetivasGlobais.filter(l => {
          // Usamos getUTCDate ou split para pegar o dia exato da string YYYY-MM-DD
          const diaProj = parseInt(l.data.split('-')[2].substring(0, 2), 10);
          return diaProj === dia;
        });
        entradasDia = projetivasDoDia.reduce((sum, l) => sum + parseFloat(l.valor), 0);
      } else {
        entradasDia = lancamentosDoDia
          .filter(l => l.tipo === 'entrada')
          .reduce((sum, l) => sum + parseFloat(l.valor), 0);
      }
        
      const saidasDia = lancamentosDoDia
        .filter(l => l.tipo === 'saida')
        .reduce((sum, l) => sum + parseFloat(l.valor), 0) -
        lancamentosDoDia
        .filter(l => l.tipo === 'estorno')
        .reduce((sum, l) => sum + parseFloat(l.valor), 0);
        
      saldoAtual += (entradasDia - saidasDia);
      saldosAcumulados.push(saldoAtual);
      entradasPorDia.push(entradasDia);
      saidasPorDia.push(saidasDia);
    }

    return {
      labels,
      datasets: [
        {
          fill: true,
          label: modoGraficoProjetivo ? 'Fluxo de Caixa (Projetivo)' : 'Fluxo de Caixa',
          data: saldosAcumulados,
          borderColor: modoGraficoProjetivo ? '#8b5cf6' : '#10b981',
          backgroundColor: modoGraficoProjetivo ? 'rgba(139, 92, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          pointRadius: 2,
          pointBackgroundColor: modoGraficoProjetivo ? '#8b5cf6' : '#10b981',
          borderWidth: 3,
          entradas: entradasPorDia,
          saidas: saidasPorDia,
          order: 1
        },
        {
          label: 'Saídas do Dia',
          data: saidasPorDia,
          borderColor: '#ef4444',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#ef4444',
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          order: 2
        }
      ]
    };
  };

  const areaChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: { size: 11, weight: '600' }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        titleColor: '#1e293b',
        titleFont: { family: "'Outfit', sans-serif", size: 14, weight: '700' },
        bodyColor: '#475569',
        bodyFont: { family: "'Inter', sans-serif", size: 13, weight: '500' },
        borderColor: 'rgba(99, 102, 241, 0.15)',
        borderWidth: 1.5,
        padding: 16,
        boxPadding: 8,
        usePointStyle: true,
        cornerRadius: 16,
        displayColors: false,
        callbacks: {
          title: function(context) {
            if (!context || !context[0]) return '';
            const dateStr = context[0].label;
            if (!dateStr.includes('/')) return dateStr;
            const [dia, mes] = dateStr.split('/');
            const mesesNomes = [
              'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
              'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
            ];
            const nomeMes = mesesNomes[parseInt(mes, 10) - 1] || mes;
            return `📅 Dia ${dia} de ${nomeMes}`;
          },
          label: function(context) {
            if (context.datasetIndex !== 0) return null;

            const index = context.dataIndex;
            const mainDataset = context.chart.data.datasets[0];
            const entries = mainDataset.entradas[index];
            const exits = mainDataset.saidas[index];
            const cumulative = context.parsed.y;
            
            const format = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

            return [
              `💼 Saldo Acumulado: ${format(cumulative)}`,
              `🟢 Entradas do Dia: ${format(entries)}`,
              `🔴 Saídas do Dia: ${format(exits)}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false }
      },
      y: {
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: value => 'R$ ' + value.toLocaleString('pt-BR')
        }
      }
    }
  };

  return (
    <div className="dashboard-container">

      <div className="dashboard-content">
        <div className="dashboard-welcome">
          <div className="welcome-text">
            <h2>Olá, {user.nome}! 👋</h2>
            <p>Aqui está o resumo da sua vida financeira hoje.</p>
          </div>
          <div className="quick-actions-btns">
            <button className="btn-quick btn-secondary-action" onClick={() => setShowModalProjetivas(true)} style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
              <span>+</span> Gerenciar Entradas Projetivas
            </button>
            <button className="btn-quick btn-import-action" onClick={() => setShowImportModal(true)} style={{ backgroundColor: '#a855f7', color: 'white' }}>
              <span>🤖</span> Importar Fatura (IA)
            </button>
            <button className="btn-quick btn-primary-action" onClick={() => { setQuickAddType(null); setFormData({...formData, tipo: 'saida', categoria_id: '', subcategoria_id: '', conta_destino_id: ''}); setShowModal(true); }}>
              <span>+</span> Adicionar Lançamento
            </button>
          </div>
        </div>

        <div className="dashboard-main-grid">
          <div className="dashboard-left-column">
            {/* Seção de Contas */}
            <div className="contas-section">
          {/* Card de Saldo Total */}
          <div className="saldo-total-card">

            <div className="saldo-total-content">
              <div className="saldo-total-header">
                <div className="saldo-label-group">
                  <span className="saldo-total-label">Patrimônio Total</span>
                  <span className="saldo-total-subtitle">Soma de todas as suas contas</span>
                </div>
                <button 
                  className="btn-toggle-valores-card"
                  onClick={() => setMostrarValores(!mostrarValores)}
                  title={mostrarValores ? 'Ocultar valores' : 'Mostrar valores'}
                >
                  {mostrarValores ? '👁️' : '🙈'}
                </button>
              </div>
              {mostrarValores ? (
                <>
                  <span className={`saldo-total-valor ${calcularSaldoTotal() >= 0 ? 'positivo' : 'negativo'}`}>
                    R$ {formatarMoeda(calcularSaldoTotal())}
                  </span>
                  
                  <div className="saldo-por-tipo">
                    {Object.entries(calcularSaldosPorTipo()).map(([tipo, valor]) => (
                      <div key={tipo} className="tipo-item">
                        <span className={`tipo-badge badge-${tipo.toLowerCase().replace(' ', '-')}`}>
                          {tipo}
                        </span>
                        <span className={`tipo-valor ${valor >= 0 ? 'positivo' : 'negativo'}`}>
                          R$ {formatarMoeda(valor)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <span className="saldo-total-valor-oculto">R$ ••••••</span>
              )}
            </div>
          </div>
          
          <div className="contas-grouped-container">
            {Object.entries(contas.reduce((acc, conta) => {
              const tipo = conta.tipo || 'Outros';
              if (!acc[tipo]) acc[tipo] = [];
              acc[tipo].push(conta);
              return acc;
            }, {})).sort(([a], [b]) => {
              const order = { 'Conta Corrente': 1, 'Cartão de Crédito': 2, 'Conta Poupança': 3, 'Conta Investimento': 4, 'Dinheiro': 5 };
              return (order[a] || 99) - (order[b] || 99);
            }).map(([tipo, contasDoTipo]) => {
              const contasOrdenadas = [...contasDoTipo].sort((a, b) => {
                const getSaldo = (conta) => {
                  if (conta.tipo === 'Cartão de Crédito') {
                    return calcularFaturaMes(conta.id).saldoRestante;
                  }
                  return parseFloat(conta.saldo_inicial) || 0;
                };
                return getSaldo(b) - getSaldo(a);
              });

              return (
                <div key={tipo} className="contas-group">
                  <h3 className="group-title">
                    {tipo === 'Cartão de Crédito' ? '💳 Cartões de Crédito' : 
                     tipo === 'Conta Corrente' ? '🏦 Contas Correntes' : 
                     tipo === 'Conta Poupança' ? '💰 Poupança' : 
                     tipo === 'Conta Investimento' ? '📈 Investimentos' : 
                     tipo === 'Dinheiro' ? '💵 Dinheiro' : tipo}
                  </h3>
                  <div className="contas-grid">
                    {contasOrdenadas.map(conta => {
                      const isSelected = selectedContas.includes(conta.id);
                      return (
                        <div 
                          key={conta.id} 
                          className={`conta-card ${isSelected ? 'selected' : ''}`} 
                          data-tipo={conta.tipo}
                          onClick={() => toggleSelectConta(conta.id)}
                        >
                          <div className="conta-header">
                            <div className="conta-info">
                              <h4>{conta.nome}</h4>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {isSelected && (
                                <span className="selection-badge">✓</span>
                              )}
                              {conta.tipo && (
                                <span className={`conta-tipo-badge badge-${conta.tipo.toLowerCase().replace(' ', '-')}`}>
                                  {conta.tipo}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="conta-saldo">
                            {mostrarValores ? (
                              <>
                                {conta.tipo === 'Cartão de Crédito' ? (
                                  <div className="cc-invoice-details">
                                    <div className="cc-detail-item">
                                      <span className="cc-detail-label">Gasto:</span>
                                      <span className="cc-detail-valor">R$ {formatarMoeda(calcularFaturaMes(conta.id).totalGasto)}</span>
                                    </div>
                                    <div className="cc-detail-item">
                                      <span className="cc-detail-label">Pago:</span>
                                      <span className="cc-detail-valor pago">R$ {formatarMoeda(calcularFaturaMes(conta.id).totalPago)}</span>
                                    </div>
                                    <div className="cc-detail-item total">
                                      <span className="cc-detail-label">Fatura:</span>
                                      <span className="cc-detail-valor fatura">R$ {formatarMoeda(calcularFaturaMes(conta.id).saldoRestante)}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span className="label">Saldo:</span>
                                    <span className={`valor ${parseFloat(conta.saldo_inicial) >= 0 ? 'positivo' : 'negativo'}`}>
                                      R$ {formatarMoeda(Number(conta.saldo_inicial) || 0)}
                                    </span>
                                  </>
                                )}
                                
                                {conta.tipo === 'Cartão de Crédito' && (
                                  <div className="limite-container">
                                    <div className="limite-info">
                                      <span>Limite: R$ {formatarMoeda(conta.limite_total)}</span>
                                      <span>{((Math.abs(Number(conta.saldo_inicial)) / Number(conta.limite_total)) * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="limite-bar-bg">
                                      <div 
                                        className="limite-bar-fill" 
                                        style={{ 
                                          width: `${Math.min(100, (Math.abs(Number(conta.saldo_inicial)) / Number(conta.limite_total)) * 100)}%`,
                                          backgroundColor: (Math.abs(Number(conta.saldo_inicial)) / Number(conta.limite_total)) > 0.8 ? '#ef4444' : '#3b82f6'
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="valor-oculto">R$ ••••••</span>
                            )}
                          </div>
                          {conta.descricao && (
                            <div className="conta-descricao">{conta.descricao}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        
        {dashboardData.length > 0 ? (
          <div className="charts-container">
            <div className="chart-container chart-line">
              <div className="chart-wrapper">
                {(!user.is_pro && !user.is_admin) && (
                  <div className="chart-lock-overlay">
                    <div className="modal-icon diamond" style={{ fontSize: '2rem', marginBottom: '10px' }}>💎</div>
                    <h5>Análise Bloqueada</h5>
                    <p>Torne-se PRO para visualizar o balanço mensal de entradas e saídas.</p>
                    <button className="btn-upgrade-chart" onClick={handleUpgradeClick}>
                      <span>✨</span> Seja PRO Agora
                    </button>
                  </div>
                )}
                <div style={{ height: '380px', width: '100%' }}>
                  <Bar data={processChartData()} options={chartOptions} />
                </div>
              </div>
            </div>

            
            {lancamentos.length > 0 && (
              <div className="chart-container chart-area">
                <div className="chart-header-compact">
                  <h4>Evolução Diária do Fluxo de Caixa {filterMes === 'TODOS' && '(Mês Atual)'}</h4>
                  <div className="chart-controls">
                    <div 
                      className={`toggle-projetivo-container ${modoGraficoProjetivo ? 'projetivo' : 'real'}`}
                      onClick={() => setModoGraficoProjetivo(!modoGraficoProjetivo)}
                      title="Alternar entre dados reais e projetivos"
                    >
                      <div className="toggle-slider"></div>
                      <span className="span-real">Real</span>
                      <span className="span-projetivo">Projetivo</span>
                    </div>

                    <button 
                      className={`btn-saldo-anterior ${usarSaldoAnterior ? 'active' : ''}`}
                      onClick={() => setUsarSaldoAnterior(!usarSaldoAnterior)}
                    >
                      {usarSaldoAnterior ? '💰 Com Saldo Anterior' : '⚪ Sem Saldo Anterior'}
                    </button>
                  </div>
                </div>
                <div className="chart-content-area" style={{ height: '350px' }}>
                  <div className="chart-wrapper">
                    {(!user.is_pro && !user.is_admin) && (
                      <div className="chart-lock-overlay">
                        <div className="modal-icon diamond" style={{ fontSize: '2rem', marginBottom: '10px' }}>💎</div>
                        <h5>Gráfico de Evolução</h5>
                        <p>Acompanhe sua evolução diária detalhada com o plano PRO.</p>
                        <button className="btn-upgrade-chart" onClick={handleUpgradeClick}>
                          <span>✨</span> Seja PRO Agora
                        </button>
                      </div>
                    )}
                    <Line data={processAreaChartData()} options={areaChartOptions} />
                  </div>
                </div>
              </div>
            )}

            {lancamentos.filter(l => l.tipo === 'saida' || l.tipo === 'estorno').length > 0 && (
              <>
                <div className="chart-pie-section">
                  <div className="chart-container chart-pie">
                    <div className="chart-header-compact">
                      <h4>Distribuição de Gastos</h4>
                    </div>
                    <div className="chart-content-pie">
                      <div className="chart-wrapper-pie">
                        <Pie data={processPieChartData()} options={pieChartOptions} />
                        <div className="chart-center-info">
                          <span className="center-label">Total Gasto</span>
                          <span className="center-value">R$ {formatarMoeda(calcularTotaisFiltrados().totalSaidas)}</span>
                        </div>
                      </div>
                      
                      <div className="custom-chart-legend">
                        {processPieChartData().labels.map((label, index) => {
                          const valor = processPieChartData().datasets[0].data[index];
                          const total = processPieChartData().datasets[0].data.reduce((a, b) => a + b, 0);
                          const percent = ((valor / total) * 100).toFixed(0);
                          return (
                            <div key={index} className="legend-item">
                              <span className="legend-dot" style={{ backgroundColor: processPieChartData().datasets[0].backgroundColor[index] }}></span>
                              <span className="legend-label" title={label}>{label}</span>
                              <span className="legend-value">
                                <span className="val-moeda">R$ {formatarMoeda(valor)}</span>
                                <span className="val-percent">{percent}%</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="chart-container chart-pie">
                    <div className="chart-header-compact">
                      <h4>Gastos por Conta</h4>
                    </div>
                    <div className="chart-content-pie">
                      <div className="chart-wrapper-pie">
                        <Pie data={processPieChartDataPorConta()} options={pieChartOptions} />
                        <div className="chart-center-info">
                          <span className="center-label">Total Gasto</span>
                          <span className="center-value">R$ {formatarMoeda(calcularTotaisFiltrados().totalSaidas)}</span>
                        </div>
                      </div>

                      <div className="custom-chart-legend">
                        {processPieChartDataPorConta().labels.map((label, index) => {
                          const valor = processPieChartDataPorConta().datasets[0].data[index];
                          const total = processPieChartDataPorConta().datasets[0].data.reduce((a, b) => a + b, 0);
                          const percent = ((valor / total) * 100).toFixed(0);
                          return (
                            <div key={index} className="legend-item">
                              <span className="legend-dot" style={{ backgroundColor: processPieChartDataPorConta().datasets[0].backgroundColor[index] }}></span>
                              <span className="legend-label" title={label}>{label}</span>
                              <span className="legend-value">
                                <span className="val-moeda">R$ {formatarMoeda(valor)}</span>
                                <span className="val-percent">{percent}%</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="right-section">
                    <div className="totals-cards">
                      <div className="total-card total-entradas">
                        <div className="card-label">
                          <span>Total de Entradas</span>
                          <div className="tooltip-container">
                            <span className="tooltip-icon">ⓘ</span>
                            <span className="tooltip-text">Soma de todas as suas receitas (entradas) recebidas no período selecionado.</span>
                          </div>
                        </div>
                        <div className="card-value">
                          {mostrarValores ? `R$ ${formatarMoeda(calcularTotaisFiltrados().totalEntradas)}` : 'R$ ••••••'}
                        </div>
                      </div>
                      <div className="total-card total-saidas">
                        <div className="card-label">
                          <span>Total de Saídas</span>
                          <div className="tooltip-container">
                            <span className="tooltip-icon">ⓘ</span>
                            <span className="tooltip-text">Soma de todas as suas despesas (saídas) liquidadas ou pendentes no período selecionado.</span>
                          </div>
                        </div>
                        <div className="card-value">
                          {mostrarValores ? `R$ ${formatarMoeda(calcularTotaisFiltrados().totalSaidas)}` : 'R$ ••••••'}
                        </div>
                        {mostrarValores && (
                          <div className="card-sub-values">
                            <div className="sub-value-item pagos" title="Total já pago">
                              <span className="dot"></span>
                              <span className="label">Pagos:</span>
                              <span className="value">R$ {formatarMoeda(calcularTotaisFiltrados().totalSaidasPagos)}</span>
                            </div>
                            <div className="sub-value-item pendentes" title="Total pendente de pagamento">
                              <span className="dot"></span>
                              <span className="label">Pendentes:</span>
                              <span className="value">R$ {formatarMoeda(calcularTotaisFiltrados().totalSaidasPendentes)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="total-card total-atraso">
                        <div className="card-label">
                          <span>⚠️ Em Atraso</span>
                          <div className="tooltip-container">
                            <span className="tooltip-icon">ⓘ</span>
                            <span className="tooltip-text">Quantidade e valor total de despesas (saídas) vencidas que ainda não foram pagas.</span>
                          </div>
                        </div>
                        <div className="card-count">{calcularEmAtraso().quantidade} lançamento{calcularEmAtraso().quantidade !== 1 ? 's' : ''}</div>
                        <div className="card-value">
                          {mostrarValores ? `R$ ${formatarMoeda(calcularEmAtraso().valorTotal)}` : 'R$ ••••••'}
                        </div>
                      </div>
                      <div className="total-card total-poupanca">
                        <div className="card-label">
                          <span>Investimento no Período</span>
                          <div className="tooltip-container">
                            <span className="tooltip-icon">ⓘ</span>
                            <span className="tooltip-text">Percentual de dinheiro economizado e investido em relação às receitas totais do período.</span>
                          </div>
                        </div>
                        <div className="card-value">
                          {mostrarValores ? `${calcularTaxaPoupanca().toFixed(1)}%` : '•••%'}
                        </div>
                        <div className="poupanca-progress-container">
                          <div className="poupanca-progress-bar">
                            <div 
                              className="poupanca-progress-fill" 
                              style={{ 
                                width: `${Math.max(0, Math.min(100, calcularTaxaPoupanca()))}%`,
                                backgroundColor: calcularTaxaPoupanca() >= 20 ? '#10b981' : calcularTaxaPoupanca() >= 0 ? '#f59e0b' : '#ef4444'
                              }}
                            ></div>
                          </div>
                          <span className="poupanca-help-text">
                            {calcularTaxaPoupanca() >= 20 ? 'Excelente poupança! 🏆' : 
                             calcularTaxaPoupanca() >= 0 ? 'Bom, tente poupar 20%.' : 'Alerta: Déficit mensal! ⚠️'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Barras horizontais por categoria e subcategoria */}
                <div className="category-bars-section">
                  <h3>📊 Valores por Categoria</h3>

                  {calcularValoresPorCategoria().length > 0 && (() => {
                    const { totalGasto, somaMetaCategorias, somaMetaSubcategorias, economiaCategorias, economiaSubcategorias } = calcularResumoMetas();
                    return (
                      <div className="metas-summary-grid">
                        <div className="meta-summary-card total-gasto">
                          <div className="card-header">
                            <span className="card-emoji">💰</span>
                            <span className="card-title">Total Gasto (Filtro)</span>
                          </div>
                          <div className="card-body">
                            <span className="card-amount">
                              {mostrarValores ? `R$ ${formatarMoeda(totalGasto)}` : 'R$ ••••••'}
                            </span>
                            <span className="card-subtitle">Despesas no filtro definido</span>
                          </div>
                        </div>

                        {somaMetaCategorias > 0 && (
                          <div className="meta-summary-card category-metas">
                            <div className="card-header">
                              <span className="card-emoji">🎯</span>
                              <span className="card-title">Meta de Categorias</span>
                            </div>
                            <div className="card-body">
                              <span className="card-amount">
                                {mostrarValores ? `R$ ${formatarMoeda(somaMetaCategorias)}` : 'R$ ••••••'}
                              </span>
                              {mostrarValores && (
                                <span className={`card-badge ${economiaCategorias > 0 ? 'warning' : 'success'}`}>
                                  {economiaCategorias > 0 
                                    ? `Economizaria R$ ${formatarMoeda(economiaCategorias)}` 
                                    : 'Dentro da meta'}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {somaMetaSubcategorias > 0 && (
                          <div className="meta-summary-card subcategory-metas">
                            <div className="card-header">
                              <span className="card-emoji">🧩</span>
                              <span className="card-title">Meta de Subcategorias</span>
                            </div>
                            <div className="card-body">
                              <span className="card-amount">
                                {mostrarValores ? `R$ ${formatarMoeda(somaMetaSubcategorias)}` : 'R$ ••••••'}
                              </span>
                              {mostrarValores && (
                                <span className={`card-badge ${economiaSubcategorias > 0 ? 'warning' : 'success'}`}>
                                  {economiaSubcategorias > 0 
                                    ? `Economizaria R$ ${formatarMoeda(economiaSubcategorias)}` 
                                    : 'Dentro da meta'}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="category-bars-container">
                    {calcularValoresPorCategoria().length > 0 ? (
                      calcularValoresPorCategoria().map((categoria, idx) => {
                        const maxValor = Math.max(...calcularValoresPorCategoria().map(c => c.total));
                        
                        let percentual = 0;
                        let isOverBudget = false;
                        
                        if (categoria.meta) {
                          percentual = (categoria.total / categoria.meta) * 100;
                          if (percentual > 100) {
                            isOverBudget = true;
                            percentual = 100;
                          }
                        } else {
                          percentual = (categoria.total / maxValor) * 100;
                        }
                        
                        return (
                          <div key={idx} className="category-bar-group">
                            <div className="category-bar-main">
                              <div className="bar-label">
                                <div className="bar-label-top">
                                  <span className="bar-categoria" style={{ color: categoria.cor }}>
                                    ● {categoria.nome}
                                    {categoria.meta && (
                                      <span title="Meta Mensal Definida" style={{ marginLeft: '4px', fontSize: '0.75rem' }}>🎯</span>
                                    )}
                                  </span>
                                  <span className="percentage-pill" style={{ backgroundColor: isOverBudget ? '#fef2f2' : `${categoria.cor}22`, color: isOverBudget ? '#ef4444' : categoria.cor }}>
                                    {((categoria.meta ? (categoria.total / categoria.meta) * 100 : (categoria.total / maxValor) * 100)).toFixed(1)}%
                                  </span>
                                </div>
                                <span className="bar-valor">
                                  <span className="val-moeda">
                                    {mostrarValores ? `R$ ${formatarMoeda(categoria.total)}` : 'R$ ••••••'}
                                  </span>
                                  {categoria.meta && mostrarValores && (
                                    <span className="val-meta">/ R$ {formatarMoeda(Number(categoria.meta))}</span>
                                  )}
                                </span>
                              </div>
                              <div className="bar-container">
                                <div 
                                  className="bar-fill" 
                                  style={{ 
                                    width: `${percentual}%`,
                                    background: isOverBudget ? '#ef4444' : categoria.cor
                                  }}
                                >
                                </div>
                              </div>
                            </div>
                            
                            {/* Subcategorias */}
                            {categoria.subcategorias.length > 0 && (
                              <div className="subcategory-bars">
                                {categoria.subcategorias.map((subcategoria, subIdx) => {
                                  let subPercentual = 0;
                                  let isSubOverBudget = false;
                                  
                                  if (subcategoria.meta) {
                                    subPercentual = (subcategoria.total / subcategoria.meta) * 100;
                                    if (subPercentual > 100) {
                                      isSubOverBudget = true;
                                      subPercentual = 100;
                                    }
                                  } else {
                                    subPercentual = (subcategoria.total / categoria.total) * 100;
                                  }
                                  
                                  return (
                                    <div key={subIdx} className="subcategory-bar">
                                      <div className="bar-label subcategory-label">
                                        <div className="bar-label-top">
                                          <span className="bar-subcategoria">
                                            └─ {subcategoria.nome}
                                            {subcategoria.meta && (
                                              <span title="Meta Mensal Definida" style={{ marginLeft: '3px', fontSize: '0.7rem' }}>🎯</span>
                                            )}
                                          </span>
                                          <span className="percentage-pill small" style={{ backgroundColor: isSubOverBudget ? '#fef2f2' : `${categoria.cor}22`, color: isSubOverBudget ? '#ef4444' : categoria.cor }}>
                                            {((subcategoria.meta ? (subcategoria.total / subcategoria.meta) * 100 : (subcategoria.total / categoria.total) * 100)).toFixed(1)}%
                                          </span>
                                        </div>
                                        <span className="bar-valor">
                                          <span className="val-moeda">
                                            {mostrarValores ? `R$ ${formatarMoeda(subcategoria.total)}` : 'R$ ••••••'}
                                          </span>
                                          {subcategoria.meta && mostrarValores && (
                                            <span className="val-meta">/ R$ {formatarMoeda(Number(subcategoria.meta))}</span>
                                          )}
                                        </span>
                                      </div>
                                      <div className="bar-container subcategory-container">
                                        <div 
                                          className="bar-fill subcategory-fill" 
                                          style={{ 
                                            width: `${subPercentual}%`,
                                            background: isSubOverBudget ? '#ef4444' : `${categoria.cor}cc`
                                          }}
                                        >
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="empty-state-card">
                        <div className="empty-state-icon">📊</div>
                        <h4>Sem dados para análise</h4>
                        <p>Nenhum dado encontrado para os filtros selecionados.</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="empty-state-card">
            <div className="empty-state-icon">🏢</div>
            <h4>Dashboard Vazio</h4>
            <p>Cadastre seus primeiros lançamentos para visualizar o resumo financeiro.</p>
          </div>
        )}

        <div className="lancamentos-section">
          <div className="lancamentos-header">
            <div className="title-with-filter">
              <h3>Lançamentos por Data</h3>
              <button 
                className={`btn-toggle-filters ${showFilters ? 'active' : ''}`}
                onClick={toggleFilters}
              >
                <span className="filter-icon">🔍</span>
                {showFilters ? 'Ocultar Filtros' : 'Filtrar'}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="filters-container active" ref={filterRef}>
              <div className="filters-grid">
                <div className="filter-group month-picker-group">
                  <label>Mês</label>
                  <div className="month-picker-wrapper" ref={monthPickerRef}>
                    <button 
                      type="button" 
                      className={`filter-select select-trigger-btn ${showMonthPicker ? 'active' : ''}`}
                      onClick={() => setShowMonthPicker(!showMonthPicker)}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="calendar-emoji">📅</span> {getFilterMesLabel()}
                      </span>
                      <span className="dropdown-arrow">▼</span>
                    </button>
                    {showMonthPicker && (
                      <div className="month-picker-popover">
                        <div className="month-picker-header">
                          <button type="button" className="btn-picker-nav" onClick={handlePrevYear}>&lt;</button>
                          <span className="picker-year-label">{pickerYear}</span>
                          <button type="button" className="btn-picker-nav" onClick={handleNextYear}>&gt;</button>
                        </div>
                        <div className="month-picker-grid">
                          {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map((mesNome, index) => {
                            const formattedMonth = `${pickerYear}-${String(index + 1).padStart(2, '0')}`;
                            const isSelected = filterMes === formattedMonth;
                            return (
                              <button
                                key={mesNome}
                                type="button"
                                className={`month-picker-cell ${isSelected ? 'active' : ''}`}
                                onClick={() => handleSelectMonth(index)}
                              >
                                {mesNome}
                              </button>
                            );
                          })}
                        </div>
                        <div className="month-picker-footer">
                          <button 
                            type="button" 
                            className={`btn-picker-all ${filterMes === 'TODOS' ? 'active' : ''}`}
                            onClick={handleSelectTodos}
                          >
                            Todos os meses
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="filter-group">
                  <label>Tipo</label>
                  <select 
                    value={filterTipo} 
                    onChange={handleFilterTipoChange}
                    className="filter-select"
                  >
                    <option value="TODOS">Todos os tipos</option>
                    <option value="entrada">Entradas</option>
                    <option value="estorno">Estornos</option>
                    <option value="neutro">Neutros</option>
                    <option value="saida">Saídas</option>
                    <option value="transferencia">Transferências</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Conta</label>
                  <select 
                    value={filterConta} 
                    onChange={(e) => setFilterConta(e.target.value)}
                    className="filter-select"
                  >
                    <option value="TODAS">Todas as contas</option>
                    {[...contas].sort((a, b) => a.nome.localeCompare(b.nome)).map(conta => (
                      <option key={conta.id} value={conta.id}>{conta.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Categoria</label>
                  <select 
                    value={filterCategoria} 
                    onChange={handleFilterCategoriaChange}
                    className="filter-select"
                  >
                    <option value="TODAS">Todas as categorias</option>
                    {[...getCategoriasDisponiveis()].sort((a, b) => a.nome.localeCompare(b.nome)).map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  </select>
                </div>

                {filterCategoria !== 'TODAS' && subcategorias.length > 0 && (
                  <div className="filter-group">
                    <label>Subcategoria</label>
                    <select 
                      value={filterSubcategoria} 
                      onChange={(e) => setFilterSubcategoria(e.target.value)}
                      className="filter-select"
                    >
                      <option value="TODAS">Todas as subcategorias</option>
                      {[...subcategorias].sort((a, b) => a.nome.localeCompare(b.nome)).map(subCat => (
                        <option key={subCat.id} value={subCat.id}>{subCat.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="filters-actions">
                <button 
                  className={`btn-filter-atraso ${filterAtraso ? 'active' : ''}`}
                  onClick={() => setFilterAtraso(!filterAtraso)}
                >
                  {filterAtraso ? '⚠️ Exibindo em atraso' : '⚠️ Mostrar em atraso'}
                </button>

                <button 
                  onClick={handleLimparFiltros}
                  className="btn-limpar-filtros"
                >
                  ✕ Limpar filtros
                </button>
              </div>
            </div>
          )}
          
          {(() => {
            let filtrados = lancamentos;
            
            if (filterMes !== 'TODOS') {
              filtrados = filtrados.filter(l => {
                const data = new Date(l.data);
                const mesLancamento = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
                return mesLancamento === filterMes;
              });
            }
            
            if (filterTipo !== 'TODOS') {
              filtrados = filtrados.filter(l => l.tipo === filterTipo);
            }

            if (filterCategoria !== 'TODAS') {
              filtrados = filtrados.filter(l => l.categoria_id == filterCategoria);
            }

            if (filterSubcategoria !== 'TODAS' && filterCategoria !== 'TODAS') {
              filtrados = filtrados.filter(l => l.subcategoria_id == filterSubcategoria);
            }

            if (filterConta !== 'TODAS') {
              filtrados = filtrados.filter(l => l.conta_id == filterConta || l.conta_destino_id == filterConta);
            }

            if (filterAtraso) {
              const hoje = new Date();
              hoje.setHours(0, 0, 0, 0);
              filtrados = filtrados.filter(l => {
                if (l.tipo !== 'saida') return false;
                const isCard = l.conta_tipo === 'Cartão de Crédito';
                if (!isCard) {
                  return !l.pago && new Date(l.data) < hoje;
                } else {
                  const conta = contas.find(c => Number(c.id) === Number(l.conta_id));
                  if (!conta || !conta.dia_vencimento) return false;
                  
                  const dataLancamento = new Date(l.data);
                  const mesAno = `${dataLancamento.getFullYear()}-${String(dataLancamento.getMonth() + 1).padStart(2, '0')}`;
                  const [year, month] = mesAno.split('-').map(Number);
                  const dataVencimento = new Date(year, month - 1, conta.dia_vencimento, 23, 59, 59);
                  if (dataVencimento >= hoje) return false;
                  
                  const { saldoRestante } = calcularFaturaMes(conta.id, mesAno);
                  return saldoRestante > 0.01;
                }
              });
            }

            if (selectedCalendarDay) {
              filtrados = filtrados.filter(l => l.data.split('T')[0] === selectedCalendarDay);
            }

            // Agrupar por data
            const agrupadoPorData = {};
            filtrados.forEach(lancamento => {
              const data = new Date(lancamento.data).toLocaleDateString('pt-BR');
              if (!agrupadoPorData[data]) {
                agrupadoPorData[data] = [];
              }
              agrupadoPorData[data].push(lancamento);
            });

            // Ordenar datas em ordem decrescente
            const datasOrdenadas = Object.keys(agrupadoPorData).sort((a, b) => {
              const [diaA, mesA, anoA] = a.split('/');
              const [diaB, mesB, anoB] = b.split('/');
              return new Date(anoB, mesB - 1, diaB) - new Date(anoA, mesA - 1, diaA);
            });

            return datasOrdenadas.length > 0 ? (
              <div className="lancamentos-by-date">
                <div className="lancamentos-list-header">
                  <div className="header-col">Tipo</div>
                  <div className="header-col">Descrição</div>
                  <div className="header-col">Valor</div>
                  <div className="header-col">Status</div>
                  <div className="header-col">Ações</div>
                </div>
                <div className="lancamentos-scroll-area">
                  {datasOrdenadas.map(data => {
                  const lancamentosDoDay = agrupadoPorData[data];
                  const totalEntrada = lancamentosDoDay
                    .filter(l => l.tipo === 'entrada' || l.tipo === 'estorno')
                    .reduce((sum, l) => sum + parseFloat(l.valor), 0);
                  const totalSaida = lancamentosDoDay
                    .filter(l => l.tipo === 'saida')
                    .reduce((sum, l) => sum + parseFloat(l.valor), 0) -
                    lancamentosDoDay
                    .filter(l => l.tipo === 'estorno')
                    .reduce((sum, l) => sum + parseFloat(l.valor), 0);
                  const saldoDia = totalEntrada - totalSaida;

                  return (
                    <div key={data} className="day-group">
                      <div className="day-header">
                        <div className="day-info">
                          <span className="day-date">{data}</span>
                          <span className={`day-balance ${saldoDia >= 0 ? 'positivo' : 'negativo'}`}>
                            Saldo: R$ {formatarMoeda(Math.abs(saldoDia))}
                          </span>
                        </div>
            <div className="day-totals">
                          <span className="entrada">↑ Entrada/Estorno: R$ {formatarMoeda(totalEntrada)}</span>
                          <span className="saida">↓ Saída: R$ {formatarMoeda(totalSaida)}</span>
                        </div>
                      </div>
                      <div className="day-items">
                        {lancamentosDoDay.map(lancamento => (
                          <div key={lancamento.id} className={`lancamento-item ${lancamento.tipo}`}>
                            <div className="item-tipo">
                              <span className={`badge ${lancamento.tipo}`}>
                                {lancamento.tipo === 'entrada' ? '↑ Entrada' : 
                                 lancamento.tipo === 'saida' ? '↓ Saída' : 
                                 lancamento.tipo === 'transferencia' ? '⇄ Transfer' : 
                                 lancamento.tipo === 'pagamento_fatura' ? '💳 Pagar Fatura' : 
                                 lancamento.tipo === 'estorno' ? '↺ Estorno' : '⊝ Neutro'}
                              </span>
                            </div>
                            <div className="item-descricao">
                              <div className="descricao-text">
                                {lancamento.descricao}
                                {(lancamento.tipo === 'transferencia' || lancamento.tipo === 'pagamento_fatura') && (
                                  <span className="transfer-info-small">
                                    ({lancamento.conta_nome} → {contas.find(c => Number(c.id) === Number(lancamento.conta_destino_id))?.nome || 'Conta Destino'})
                                  </span>
                                )}
                              </div>
                              <div className="categoria-badges-inline">
                                {lancamento.categoria_nome && (
                                  <span 
                                    className="categoria-badge-small"
                                    style={{ backgroundColor: lancamento.categoria_cor || '#7c3aed' }}
                                    title={lancamento.categoria_nome}
                                  >
                                    {lancamento.categoria_nome}
                                  </span>
                                )}
                                {lancamento.subcategoria_nome && (
                                  <span 
                                    className="subcategoria-badge-small"
                                    style={{ backgroundColor: lancamento.subcategoria_cor || '#7c3aed' }}
                                    title={lancamento.subcategoria_nome}
                                  >
                                    {lancamento.subcategoria_nome}
                                  </span>
                                )}
                                <span className="conta-badge-inline" title="Conta">
                                  🏦 {lancamento.conta_nome || '-'}
                                </span>
                              </div>
                            </div>
                            <div className={`item-valor ${lancamento.tipo === 'entrada' || lancamento.tipo === 'estorno' ? 'valor-positivo' : lancamento.tipo === 'saida' ? 'valor-negativo' : ''}`}>
                              {mostrarValores ? `R$ ${formatarMoeda(Number(lancamento.valor) || 0)}` : 'R$ ••••'}
                            </div>
                            <div className="item-pago">
                              {lancamento.tipo === 'saida' && lancamento.conta_tipo !== 'Cartão de Crédito' && (
                                <button 
                                  className={`btn-toggle-pago-item ${lancamento.pago ? 'pago' : 'pendente'}`}
                                  onClick={() => handleTogglePago(lancamento)}
                                  title={lancamento.pago ? 'Marcar como não pago' : 'Marcar como pago'}
                                >
                                  <span className={`badge-pago ${lancamento.pago ? 'pago' : 'pendente'}`}>
                                    {lancamento.pago ? (
                                      <>
                                        <span className="status-dot pago"></span>
                                        Pago
                                      </>
                                    ) : (
                                      <>
                                        <span className="status-dot pendente"></span>
                                        Não pago
                                      </>
                                    )}
                                  </span>
                                </button>
                              )}
                              {lancamento.conta_tipo === 'Cartão de Crédito' && (
                                <span className="badge-pago card-cc-badge">
                                  💳 Cartão
                                </span>
                              )}
                            </div>
                            <div className="item-actions">
                              <button className="btn-action-edit" onClick={() => handleEdit(lancamento)} title="Editar">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 20h9"/>
                                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                                </svg>
                              </button>
                              <button className="btn-action-delete" onClick={() => handleDelete(lancamento)} title="Excluir">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18"/>
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-state-card" style={{ marginTop: '20px' }}>
                <div className="empty-state-icon">🔍</div>
                <h4>Nenhum lançamento</h4>
                <p>Nenhum lançamento encontrado com os filtros selecionados.</p>
                <button className="btn-limpar-filtros" onClick={() => {
                  setFilterMes('TODOS');
                  setFilterTipo('TODOS');
                  setFilterCategoria('TODAS');
                  setFilterSubcategoria('TODAS');
                  setFilterConta('TODAS');
                  setFilterAtraso(false);
                  setSelectedCalendarDay(null);
                }} style={{ marginTop: '10px' }}>
                  Limpar Filtros
                </button>
              </div>
            );
          })()}
          </div> {/* Fim de lancamentos-section */}
          </div> {/* Fim de contas-section */}
          </div> {/* Fim de dashboard-left-column */}
          
          <div className="dashboard-right-sidebar">
            {/* Card de Saúde Financeira */}
            {(() => {
              const scoreData = calcularScoreSaudeFinanceira();
              const radius = 40;
              const circumference = 2 * Math.PI * radius;
              const strokeDashoffset = circumference - (scoreData.score / 100) * circumference;

              const isProUser = user.is_pro || user.is_admin;

              return (
                <div className="health-score-card">
                  <div className="health-score-header">
                    <h4>Saúde Financeira</h4>
                    {!isProUser ? (
                      <span className="health-status-pill pro-tag">💎 PRO</span>
                    ) : (
                      <span className={`health-status-pill ${scoreData.status}`}>
                        {scoreData.status.toUpperCase()}
                      </span>
                    )}
                  </div>
                  
                  <div className="health-score-content" style={!isProUser ? { filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none' } : {}}>
                    <div className="score-circle-wrapper">
                      <svg className="score-circle-svg" viewBox="0 0 100 100">
                        <circle className="score-circle-bg" cx="50" cy="50" r={radius} />
                        <circle 
                          className="score-circle-progress" 
                          cx="50" 
                          cy="50" 
                          r={radius} 
                          stroke={scoreData.cor}
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                        />
                      </svg>
                      <div className="score-text-overlay">
                        <span className="score-num">{scoreData.score}</span>
                        <span className="score-pts">pontos</span>
                      </div>
                    </div>
                    
                    <p className="health-recommendation">
                      {scoreData.recomendacao}
                    </p>
                  </div>

                  {!isProUser && (
                    <div className="health-score-lock-overlay">
                      <div className="lock-icon">💎</div>
                      <h5>Diagnóstico Bloqueado</h5>
                      <p>Ative o plano PRO para ter acesso ao score e orientações da sua saúde financeira.</p>
                      <button className="btn-upgrade-score" onClick={handleUpgradeClick}>
                        <span>✨</span> Seja PRO Agora
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Calendário Financeiro */}
            {gerarCalendario()}
          </div> {/* Fim de dashboard-right-sidebar */}
        </div> {/* Fim de dashboard-main-grid */}
      </div> {/* Fim de dashboard-content */}

      {showModal && (
        <div className="modal">
          <div className="modal-content modal-lancamento premium-card">
            <button className="btn-close-modal" onClick={handleCloseModal}>✕</button>
            <div className="modal-body">
              <h3>{editingLancamento ? 'Editar Lançamento' : 'Novo Lançamento'}</h3>
            <form onSubmit={handleSubmit}>
              
              <div className="form-group col-span-2">
                <label>Direção do lançamento *</label>
                <div className="btn-group">
                  <button
                    type="button"
                    className={formData.tipo === 'entrada' ? 'active' : ''}
                    onClick={() => {
                      setFormData({...formData, tipo: 'entrada', categoria_id: '', subcategoria_id: '', conta_destino_id: '', pago: false});
                      setQuickAddType(null);
                    }}
                  >
                    Entrada
                  </button>
                  <button
                    type="button"
                    className={formData.tipo === 'saida' ? 'active' : ''}
                    onClick={() => {
                      setFormData({...formData, tipo: 'saida', categoria_id: '', subcategoria_id: '', conta_destino_id: ''});
                      setQuickAddType(null);
                    }}
                  >
                    Saída
                  </button>
                  <button
                    type="button"
                    className={formData.tipo === 'transferencia' ? 'active' : ''}
                    onClick={() => {
                      setFormData({...formData, tipo: 'transferencia', categoria_id: '', subcategoria_id: '', pago: false});
                      setQuickAddType(null);
                    }}
                  >
                    Transfer
                  </button>
                  <button
                    type="button"
                    className={formData.tipo === 'neutro' ? 'active' : ''}
                    onClick={() => {
                      setFormData({...formData, tipo: 'neutro', categoria_id: '', subcategoria_id: '', conta_destino_id: '', pago: false});
                      setQuickAddType(null);
                    }}
                  >
                    Neutro
                  </button>
                  <button
                    type="button"
                    className={formData.tipo === 'pagamento_fatura' ? 'active' : ''}
                    onClick={() => {
                      setFormData({
                        ...formData, 
                        tipo: 'pagamento_fatura', 
                        descricao: 'Pagamento de Fatura',
                        categoria_id: '', 
                        subcategoria_id: '', 
                        conta_destino_id: '', 
                        pago: true
                      });
                      setQuickAddType(null);
                    }}
                    style={{ backgroundColor: formData.tipo === 'pagamento_fatura' ? '#10b981' : '' }}
                  >
                    💳 Pagar Fatura
                  </button>
                  <button
                    type="button"
                    className={formData.tipo === 'estorno' ? 'active' : ''}
                    onClick={() => {
                      const firstCard = contas.find(c => c.tipo === 'Cartão de Crédito');
                      setFormData({
                        ...formData,
                        tipo: 'estorno',
                        categoria_id: '',
                        subcategoria_id: '',
                        conta_id: firstCard ? firstCard.id.toString() : '',
                        conta_destino_id: '',
                        pago: false
                      });
                      setQuickAddType(null);
                    }}
                    style={{ backgroundColor: formData.tipo === 'estorno' ? '#7c3aed' : '' }}
                  >
                    ↺ Estorno
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{formData.parcelado ? 'Valor Total (R$) *' : 'Valor (R$) *'}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({...formData, valor: e.target.value})}
                    placeholder="0,00"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Data *</label>
                  <input
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({...formData, data: e.target.value})}
                    required
                  />
                  {formData.tipo === 'saida' && selectedContaInfo?.tipo === 'Cartão de Crédito' && selectedContaInfo?.dia_vencimento && (
                    <small className="help-text" style={{ color: '#7c3aed', fontWeight: 600, marginTop: '4px', display: 'block' }}>
                      💡 A data será ajustada para o dia {selectedContaInfo.dia_vencimento} (vencimento do cartão).
                    </small>
                  )}
                </div>
              </div>

              {formData.tipo === 'saida' && (
                <div className="form-group">
                  <label>Status de Pagamento</label>
                  <div className="btn-group">
                    <button
                      type="button"
                      className={formData.pago ? 'active' : ''}
                      onClick={() => setFormData({...formData, pago: true})}
                    >
                      Pago
                    </button>
                    <button
                      type="button"
                      className={!formData.pago ? 'active' : ''}
                      onClick={() => setFormData({...formData, pago: false})}
                    >
                      Não pago
                    </button>
                  </div>
                </div>
              )}

              {(formData.tipo === 'saida' || formData.tipo === 'entrada' || formData.tipo === 'neutro') && (
                <div className="form-group">
                  <label>Repetição</label>
                  <div className="btn-group">
                    <button
                      type="button"
                      className={(!formData.parcelado && !formData.recorrente) ? 'active' : ''}
                      onClick={() => setFormData({...formData, parcelado: false, recorrente: false, num_parcelas: 1})}
                    >
                      Único
                    </button>
                    {formData.tipo === 'saida' && (
                      <button
                        type="button"
                        className={formData.parcelado ? 'active' : ''}
                        onClick={() => setFormData({...formData, parcelado: true, recorrente: false, num_parcelas: parseInt(formData.num_parcelas) > 1 ? formData.num_parcelas : 2})}
                      >
                        Parcelado
                      </button>
                    )}
                    <button
                      type="button"
                      className={formData.recorrente ? 'active' : ''}
                      onClick={() => setFormData({...formData, recorrente: true, parcelado: false, num_parcelas: 1})}
                    >
                      Fixo/Recorrente
                    </button>
                  </div>
                </div>
              )}

              {formData.tipo === 'saida' && formData.parcelado && (
                <div className="form-group col-span-2">
                  <label>Número de Parcelas *</label>
                  <input
                    type="number"
                    min="2"
                    max="99"
                    step="1"
                    value={formData.num_parcelas}
                    onChange={(e) => {
                      let val = e.target.value;
                      // Remover zeros à esquerda e caracteres não numéricos
                      val = val.replace(/^0+/, '');
                      if (val === '' || (!val.includes('.') && !val.includes(','))) {
                        setFormData({...formData, num_parcelas: val});
                      }
                    }}
                    required
                  />
                  {parseInt(formData.num_parcelas) > 1 && (
                    <small className="help-text" style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      O valor total (R$ {formData.valor || '0,00'}) será dividido em {formData.num_parcelas} parcelas de R$ {(Math.floor((parseFloat(formData.valor || 0) / (parseInt(formData.num_parcelas) || 1)) * 100) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                    </small>
                  )}
                  {(!formData.num_parcelas || parseInt(formData.num_parcelas) < 2) && (
                    <small className="help-text" style={{ display: 'block', marginTop: '5px', color: '#dc2626' }}>
                      ⚠️ Mínimo de 2 parcelas.
                    </small>
                  )}
                </div>
              )}

              <div className={`form-group ${formData.tipo === 'transferencia' || formData.tipo === 'pagamento_fatura' ? 'col-span-2' : ''}`}>
                <label>Descrição *</label>
                <input
                  type="text"
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder="Ex: Aluguel, Salário..."
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  {formData.tipo === 'transferencia' ? 'Conta de Origem *' : 
                   formData.tipo === 'pagamento_fatura' ? 'Retirar de qual conta? *' : 'Conta *'}
                </label>
                <select
                  value={formData.conta_id}
                  onChange={(e) => setFormData({...formData, conta_id: e.target.value})}
                  required
                >
                  <option value="">Selecione uma conta</option>
                  {contas
                    .filter(c => {
                      if (formData.tipo === 'estorno') return c.tipo === 'Cartão de Crédito';
                      if (formData.tipo === 'pagamento_fatura') return c.tipo !== 'Cartão de Crédito';
                      return true;
                    })
                    .map(conta => (
                      <option key={conta.id} value={conta.id}>{conta.nome} (R$ {formatarMoeda(Number(conta.saldo_inicial))})</option>
                    ))}
                </select>
              </div>

              {(formData.tipo === 'transferencia' || formData.tipo === 'pagamento_fatura') && (
                <div className="form-group">
                  <label>
                    {formData.tipo === 'transferencia' ? 'Conta de Destino *' : 'Qual Cartão de Crédito? *'}
                  </label>
                  <select
                    value={formData.conta_destino_id}
                    onChange={(e) => setFormData({...formData, conta_destino_id: e.target.value})}
                    required
                  >
                    <option value="">{formData.tipo === 'transferencia' ? 'Selecione a conta de destino' : 'Selecione o cartão'}</option>
                    {contas
                      .filter(c => {
                        if (formData.tipo === 'pagamento_fatura') return c.tipo === 'Cartão de Crédito';
                        return c.id.toString() !== formData.conta_id.toString();
                      })
                      .map(conta => (
                        <option key={conta.id} value={conta.id}>{conta.nome} (R$ {formatarMoeda(Number(conta.saldo_inicial))})</option>
                      ))}
                  </select>
                </div>
              )}

              {formData.tipo !== 'transferencia' && formData.tipo !== 'pagamento_fatura' && (
                <>
                  <div className="form-group col-span-2">
                    <label>Classificação</label>
                    <div className="categoria-buttons">
                      {categorias
                        .filter(cat => formData.tipo === 'estorno' ? cat.tipo === 'saida' : cat.tipo === formData.tipo)
                        .map(categoria => (
                          <button
                            key={categoria.id}
                            type="button"
                            className={String(formData.categoria_id) === String(categoria.id) ? 'active' : ''}
                            onClick={() => {
                              setFormData({...formData, categoria_id: categoria.id.toString(), subcategoria_id: ''});
                              loadSubcategoriasPorCategoria(categoria.id);
                            }}
                          >
                            {categoria.nome}
                          </button>
                        ))}
                    </div>
                  </div>

                  {formData.categoria_id && subcategorias.length > 0 && (
                    <div className="form-group col-span-2">
                      <label>Subcategoria</label>
                      <select
                        value={formData.subcategoria_id}
                        onChange={(e) => setFormData({...formData, subcategoria_id: e.target.value})}
                      >
                        <option value="">Nenhuma</option>
                        {subcategorias.map(sub => (
                          <option key={sub.id} value={sub.id}>{sub.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>Cancelar</button>
                <button type="submit" className="btn-primary">
                  {editingLancamento ? 'Salvar Alterações' : 'Confirmar Lançamento'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
      {showModalProjetivas && (
        <div className="modal">
          <div className="modal-content modal-projetivas premium-card" style={{ maxWidth: '750px', width: '95%' }}>
            <button className="btn-close-modal" onClick={() => setShowModalProjetivas(false)}>✕</button>
            <div className="modal-body">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div className="header-info">
                  <h3 style={{ margin: 0 }}>Gerenciar Entradas Projetivas</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Apenas o dia será considerado.</p>
                </div>
              <div className="total-badge-projetivas" style={{ background: '#f5f3ff', padding: '10px 20px', borderRadius: '12px', textAlign: 'right' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>Total Projetado:</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#5b21b6' }}>R$ {totalProjetivas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              </div>
            </div>
            
            {entradasProjetivas.length > 0 && (
              <div className="projetivas-list-container">
                <div className="projetivas-scroll-area">
                  {entradasProjetivas.map(proj => (
                    <div key={proj.id} className="projetiva-item-card">
                      <div className="proj-row-top">
                        <span className="proj-dia-badge">Dia {parseInt(proj.data.split('-')[2].substring(0, 2))}</span>
                        <span className="proj-desc-main">{proj.descricao}</span>
                      </div>
                      <div className="proj-row-bottom">
                        <span className="proj-valor-main">R$ {parseFloat(proj.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        <button 
                          type="button" 
                          className="btn-delete-proj-v2"
                          onClick={() => handleDeleteProjetivaDb(proj.id)}
                          title="Remover projeção"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="projetivas-novas" style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ margin: 0 }}>Adicionar Novas Projeções</h4>
                <button type="button" className="btn-add-line" onClick={handleAddProjetivaRow}>
                  <span>+</span> Adicionar Linha
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {projetivasList.map((item, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input type="date" value={item.data} onChange={(e) => handleProjetivaChange(index, 'data', e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }} />
                    <input type="text" placeholder="Descrição" value={item.descricao} onChange={(e) => handleProjetivaChange(index, 'descricao', e.target.value)} style={{ flex: 2, padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }} />
                    <input type="number" step="0.01" placeholder="Valor" value={item.valor} onChange={(e) => handleProjetivaChange(index, 'valor', e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }} />
                    {projetivasList.length > 1 && (
                      <button type="button" onClick={() => handleRemoveProjetivaRow(index)} style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer' }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button type="button" className="btn-cancel" onClick={() => setShowModalProjetivas(false)}>Fechar</button>
              <button type="button" className="btn-save" onClick={handleSaveProjetivas} style={{ backgroundColor: '#8b5cf6' }}>
                Salvar Projeções
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Confirmação de Pagamento */}
      {showPagoModal && pagoItem && (
        <div className="modal">
          <div className="modal-content payment-modal premium-card">
            <button className="btn-close-modal" onClick={() => setShowPagoModal(false)}>✕</button>
            <div className="modal-body">
              <div className="modal-icon info">💳</div>
              <h3>Confirmar Status?</h3>
            <p className="payment-warning">
              Deseja alterar o status para <strong>{!pagoItem.pago ? 'PAGO' : 'PENDENTE'}</strong>?
            </p>
            
            <div className="payment-details-box">
              <div className="detail-row">
                <span className="detail-label">Lançamento:</span>
                <span className="detail-value">{pagoItem.descricao}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Valor:</span>
                <span className="detail-value highlight">R$ {formatarMoeda(Number(pagoItem.valor))}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Conta:</span>
                <span className="detail-value">{pagoItem.conta_nome}</span>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowPagoModal(false)}>Cancelar</button>
              <button 
                className={`btn-confirm-payment ${!pagoItem.pago ? 'pay' : 'unpay'}`} 
                onClick={confirmTogglePago}
              >
                {!pagoItem.pago ? 'Sim, Marcar como Pago' : 'Sim, Marcar como Pendente'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Barra de Resumo de Contas Selecionadas */}
      {selectedContas.length > 0 && (() => {
        const { totalNormal, totalCC, hasNormal, hasCC } = calcularSomaSelecionadas();
        let displaySum = '';
        if (hasNormal && !hasCC) {
          displaySum = `Soma: R$ ${formatarMoeda(totalNormal)}`;
        } else if (!hasNormal && hasCC) {
          displaySum = `Soma Faturas: R$ ${formatarMoeda(totalCC)}`;
        } else {
          const netTotal = totalNormal - totalCC;
          displaySum = `Saldo Líquido: R$ ${formatarMoeda(netTotal)}`;
        }

        return (
          <div className="selected-accounts-summary-bar">
            <div className="summary-bar-content">
              <span className="selected-count-label">
                <strong>{selectedContas.length}</strong> {selectedContas.length === 1 ? 'conta selecionada' : 'contas selecionadas'}
              </span>
              <div className="summary-divider"></div>
              <span className="selected-sum-value">
                {mostrarValores ? (
                  <strong>{displaySum}</strong>
                ) : (
                  <strong>Soma: R$ ••••••</strong>
                )}
              </span>
              <button className="btn-clear-selection" onClick={() => setSelectedContas([])}>
                Desmarcar Tudo
              </button>
            </div>
          </div>
        );
      })()}

      {/* Botões Flutuantes */}
      <div className="fab-container">
        <button 
          className="fab-item fab-refresh" 
          onClick={handleRefreshData}
          title="Atualizar Dados"
        >
          🔄
        </button>
        <button 
          className="fab-item fab-add" 
          onClick={() => { setQuickAddType(null); setFormData({...formData, tipo: 'saida', categoria_id: '', subcategoria_id: '', conta_destino_id: ''}); setShowModal(true); }}
          title="Novo Lançamento"
        >
          ➕
        </button>
        {(!user.is_pro && !user.is_admin) && (
          <button 
            className="fab-item fab-pro-badge" 
            onClick={() => handleUpgradeClick()}
            title="Seja PRO"
          >
            💎
          </button>
        )}
        
        <button 
          className={`fab-item fab-filter ${showFilters ? 'active' : ''}`} 
          onClick={toggleFilters}
          title="Filtros"
        >
          🔍
        </button>
        
        {showScrollTop && (
          <button 
            className="fab-item fab-scroll-top" 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            title="Voltar ao topo"
          >
            ↑
          </button>
        )}
      </div>

      {/* Modal de Exclusão Global */}
      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content delete-modal">
            <div className="modal-icon warning">⚠️</div>
            <h3>Excluir Item?</h3>
            <p>Deseja realmente excluir este item permanentemente?</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
              <button className="btn-delete-confirm" onClick={confirmDelete}>Sim, Excluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Notificação Toast */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <span className="toast-icon">{toast.type === 'success' ? '✅' : '❌'}</span>
            <span className="toast-message">{toast.message}</span>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}
      {/* Modal de Limite PRO / Checkout */}
      {showProLimitModal && (
        <div className="modal">
          <div className="modal-content premium-card pro-limit-modal checkout-modal">
            <button className="btn-close-modal" onClick={() => setShowProLimitModal(false)}>✕</button>
            
            {!pixData ? (
              <>
                <div className="modal-icon diamond">💎</div>
                <h3>Upgrade para o Plano PRO</h3>
                <div className="pro-limit-content">
                  <p>{proLimitMessage || 'Acesse recursos ilimitados e impulsione sua gestão financeira.'}</p>
                  <div className="pro-benefit-box">
                    <p><strong>Benefícios Exclusivos:</strong></p>
                    <ul>
                      <li>✨ Lançamentos e Contas ilimitados</li>
                      <li>📊 Gráficos de Evolução Diária e Fluxo</li>
                      <li>🏦 Gestão de múltiplas contas bancárias</li>
                      <li>🏷️ Categorias globais personalizadas</li>
                    </ul>
                  </div>
                  
                  <div className="price-tag">
                    <span className="currency">R$</span>
                    <span className="amount">{parseFloat(subscriptionPrice).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    <span className="period">/mês</span>
                  </div>

                  <button 
                    className="btn-save btn-upgrade-confirm" 
                    onClick={handleStartSubscription}
                    disabled={isGeneratingPix}
                  >
                    {isGeneratingPix ? 'Gerando PIX...' : 'Assinar Agora via PIX'}
                  </button>
                </div>
              </>
            ) : paymentStatus === 'PAID' ? (
              <div className="success-checkout">
                <div className="success-icon">🎉</div>
                <h3>Pagamento Confirmado!</h3>
                <p>Seu acesso PRO foi liberado por 30 dias. Aproveite todos os recursos!</p>
                <div className="loading-spinner-small"></div>
                <p className="small-text">Recarregando o sistema...</p>
              </div>
            ) : (
              <div className="pix-checkout-area">
                <h3>Finalize seu Pagamento</h3>
                <p>Escaneie o QR Code abaixo ou utilize o Copia e Cola.</p>
                
                <div className="qrcode-container">
                  <img src={pixData.imagemQrcode} alt="QR Code PIX" />
                </div>
                
                <div className="copia-e-cola-box">
                  <label>Código Copia e Cola:</label>
                  <div className="copy-input">
                    <input type="text" readOnly value={pixData.copiaECola} id="pix-copy-input" />
                    <button onClick={() => {
                      const copyText = document.getElementById("pix-copy-input");
                      copyText.select();
                      document.execCommand("copy");
                      triggerToast('Código copiado!');
                    }}>Copiar</button>
                  </div>
                </div>

                <div className="polling-status">
                  <div className="spinner"></div>
                  <span>Aguardando confirmação do pagamento...</span>
                </div>

                <div className="payment-info-footer">
                  <p>Valor: <strong>R$ {parseFloat(subscriptionPrice).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></p>
                  <p>Validade: 30 dias de acesso PRO</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Wizard de Boas-vindas */}
      {showWizard && <WelcomeWizard onFinish={handleFinishWizard} />}

      {/* Modal de Importação de Fatura */}
      <InvoiceImportModal 
        isOpen={showImportModal} 
        onClose={() => setShowImportModal(false)} 
        onSuccess={(msg) => { triggerToast(msg); loadDashboard(); loadLancamentos(); loadContas(); }} 
      />
    </div>
  );
}

export default Dashboard;
